import React, { useState, useEffect } from "react";
import { supabase } from "./supabase";
import Toast from "./components/Toast";
import ConfirmModal from "./components/ConfirmModal";
import "./style.css";

// Função para calcular tempo relativo
const getTimeAgo = (date) => {
  const now = new Date();
  const past = new Date(date);
  const diffInSeconds = Math.floor((now - past) / 1000);
  
  if (diffInSeconds < 60) return "agora";
  if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} min`;
  }
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours}h`;
  }
  if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days}d`;
  }
  if (diffInSeconds < 2592000) {
    const weeks = Math.floor(diffInSeconds / 604800);
    return `${weeks}sem`;
  }
  if (diffInSeconds < 31536000) {
    const months = Math.floor(diffInSeconds / 2592000);
    return `${months}mes`;
  }
  const years = Math.floor(diffInSeconds / 31536000);
  return `${years}a`;
};

function StartUps({ user, onNav, onBack }) {
  const [startups, setStartups] = useState([]);
  const [myStartups, setMyStartups] = useState([]);
  const [followedStartups, setFollowedStartups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("explore"); // explore, my-startups, following, updates, communities, community-detail
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedStartup, setSelectedStartup] = useState(null);
  const [communityStartup, setCommunityStartup] = useState(null); // Startup da comunidade sendo visualizada
  const [showJobForm, setShowJobForm] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showComments, setShowComments] = useState({});
  const [commentText, setCommentText] = useState({});
  const [updateLikes, setUpdateLikes] = useState({});
  const [updateComments, setUpdateComments] = useState({});
  
  // Estados para Comunidade da Startup
  const [communityPosts, setCommunityPosts] = useState([]);
  const [communityPostText, setCommunityPostText] = useState("");
  const [loadingCommunity, setLoadingCommunity] = useState(false);
  const [communityReactions, setCommunityReactions] = useState({});
  const [communityComments, setCommunityComments] = useState({});
  const [communityCommentText, setCommunityCommentText] = useState({});
  const [showReactionPicker, setShowReactionPicker] = useState(null);
  const [collapsedComments, setCollapsedComments] = useState({}); // Para minimizar comentários
  const [replyingTo, setReplyingTo] = useState({}); // Para controlar qual comentário está sendo respondido
  const [openMenus, setOpenMenus] = useState({}); // Para controlar menus de 3 pontinhos
  const [longPressTimer, setLongPressTimer] = useState(null); // Para long press no mobile
  
  // Feed global de comunidades (todas as startups seguidas)
  const [allCommunityPosts, setAllCommunityPosts] = useState([]);
  const [loadingAllCommunity, setLoadingAllCommunity] = useState(false);
  const [openComments, setOpenComments] = useState({});
  
  // Dados do perfil do usuário
  const [profileData, setProfileData] = useState(null);
  
  // Notificações
  const [unreadMessages, setUnreadMessages] = useState([]);
  const [messageNotification, setMessageNotification] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    website: "",
    logo_url: "",
    location: ""
  });

  const [updateData, setUpdateData] = useState({
    title: "",
    content: ""
  });

  const [jobData, setJobData] = useState({
    title: "",
    description: "",
    requirements: "",
    location: "",
    type: "full-time",
    salary_range: ""
  });

  // Detectar se é mobile
  const isMobile = window.innerWidth <= 768;

  useEffect(() => {
    if (user) {
      loadData();
      loadProfileData();
    }
  }, [user]);

  // Carregar interações quando mudar para aba de atualizações
  useEffect(() => {
    if (activeTab === "updates" && followedStartups.length > 0) {
      followedStartups.forEach(startup => {
        if (startup.updates && startup.updates.length > 0) {
          startup.updates.forEach(update => {
            loadUpdateInteractions(update.id);
          });
        }
      });
    }
  }, [activeTab]);

  // Carregar reações e comentários dos posts da comunidade
  useEffect(() => {
    if (communityPosts.length > 0) {
      communityPosts.forEach(post => {
        loadCommunityReactions(post.id);
        loadCommunityComments(post.id);
      });
    }
  }, [communityPosts.length]);

  // Carregar feed de comunidades quando mudar para a aba
  useEffect(() => {
    if (activeTab === "communities" && followedStartups.length > 0) {
      loadAllCommunityPosts();
    }
  }, [activeTab, followedStartups.length]);

  const showToast = (message, type = "info") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadProfileData = async () => {
    try {
      const { data, error } = await supabase
        .from("users")
        .select("*")
        .eq("id", user.id)
        .single();

      if (error) throw error;
      if (data) {
        setProfileData(data);
      }
    } catch (error) {
      console.error("Erro ao carregar dados do perfil:", error);
    }
  };

  // Buscar mensagens não lidas
  useEffect(() => {
    if (!user?.id) return;

    const fetchUnreadMessages = async () => {
      try {
        const { data, error } = await supabase
          .from('messages')
          .select('*')
          .or(`chat_id.like.%${user.id}%`)
          .eq('read', false)
          .neq('sender_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        
        if (data && data.length > 0) {
          setUnreadMessages(data);
        }
      } catch (error) {
        console.error("Erro ao buscar mensagens não lidas:", error);
      }
    };

    fetchUnreadMessages();

    // Subscrever a novas mensagens em tempo real
    const channel = supabase
      .channel('user-messages-startups')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
      }, async (payload) => {
        const newMsg = payload.new;
        const chatId = newMsg.chat_id;
        if (chatId && chatId.includes(user.id) && newMsg.sender_id !== user.id) {
          setUnreadMessages(prev => [newMsg, ...prev]);
          
          // Buscar dados do remetente
          const { data: sender } = await supabase
            .from('users')
            .select('*')
            .eq('id', newMsg.sender_id)
            .single();

          if (sender) {
            // Mostrar notificação toast
            setMessageNotification({
              id: newMsg.id,
              message: newMsg.content,
              senderName: sender.nome,
              senderPhoto: sender.avatar || 'https://via.placeholder.com/150',
              senderData: { ...sender, userId: sender.id },
              timestamp: new Date(newMsg.created_at).getTime()
            });

            // Vibração
            if (navigator.vibrate) {
              navigator.vibrate([200, 100, 200]);
            }

            // Som de notificação
            try {
              const audioContext = new (window.AudioContext || window.webkitAudioContext)();
              const oscillator = audioContext.createOscillator();
              const gainNode = audioContext.createGain();
              
              oscillator.connect(gainNode);
              gainNode.connect(audioContext.destination);
              
              oscillator.type = 'sine';
              oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
              
              gainNode.gain.setValueAtTime(0, audioContext.currentTime);
              gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01);
              gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
              
              oscillator.start(audioContext.currentTime);
              oscillator.stop(audioContext.currentTime + 0.5);
            } catch (e) {
              console.error("Erro ao tocar som:", e);
            }

            // Auto-fechar notificação após 5 segundos
            setTimeout(() => {
              setMessageNotification(null);
            }, 5000);
          }
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadAllStartups(),
        loadMyStartups(),
        loadFollowedStartups()
      ]);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      showToast("Erro ao carregar dados", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadAllStartups = async () => {
    const { data, error } = await supabase
      .from("startups")
      .select(`
        *,
        owner:users!startups_owner_id_fkey(id, nome),
        followers:startup_followers(count),
        updates:startup_updates(count),
        jobs:startup_jobs(count)
      `)
      .order("created_at", { ascending: false });

    if (!error && data) {
      setStartups(data);
    }
  };

  const loadMyStartups = async () => {
    const { data, error } = await supabase
      .from("startups")
      .select(`
        *,
        followers:startup_followers(count),
        updates:startup_updates(count),
        jobs:startup_jobs(count),
        community_posts:startup_community_posts(count)
      `)
      .eq("owner_id", user.id)
      .order("created_at", { ascending: false });

    if (!error && data) {
      setMyStartups(data);
    }
  };

  const loadFollowedStartups = async () => {
    const { data, error } = await supabase
      .from("startup_followers")
      .select(`
        startup:startups(
          *,
          owner:users!startups_owner_id_fkey(id, nome),
          followers:startup_followers(count),
          community_posts:startup_community_posts(count),
          updates:startup_updates(
            id,
            title,
            content,
            created_at,
            author:users!startup_updates_author_id_fkey(id, nome)
          ),
          jobs:startup_jobs(
            id,
            title,
            description,
            requirements,
            location,
            type,
            salary_range,
            is_active,
            created_at
          )
        )
      `)
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (!error && data) {
      const startups = data.map(item => item.startup);
      setFollowedStartups(startups);
      
      // Carregar interações para todas as atualizações
      startups.forEach(startup => {
        if (startup.updates && startup.updates.length > 0) {
          startup.updates.forEach(update => {
            loadUpdateInteractions(update.id);
          });
        }
      });
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast("Por favor, selecione uma imagem válida", "error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showToast("Imagem muito grande. Máximo 5MB", "error");
      return;
    }

    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      setLogoPreview(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadLogo = async () => {
    if (!logoFile) return null;

    try {
      const fileExt = logoFile.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `startup-logos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('startup-images')
        .upload(filePath, logoFile);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('startup-images')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error("Erro ao fazer upload:", error);
      showToast("Erro ao enviar logo", "error");
      return null;
    }
  };

  const handleCreateStartup = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.description) {
      showToast("Preencha nome e descrição", "error");
      return;
    }

    try {
      let logoUrl = formData.logo_url;
      
      // Se há um arquivo, fazer upload
      if (logoFile) {
        const uploaded = await uploadLogo();
        if (uploaded) logoUrl = uploaded;
      }

      const { data, error } = await supabase
        .from("startups")
        .insert([{
          ...formData,
          logo_url: logoUrl,
          owner_id: user.id
        }])
        .select()
        .single();

      if (error) throw error;

      showToast("StartUp criada com sucesso!", "success");
      setShowCreateForm(false);
      setFormData({
        name: "",
        description: "",
        category: "",
        website: "",
        logo_url: "",
        location: ""
      });
      setLogoFile(null);
      setLogoPreview(null);
      loadData();
      // Abrir a startup criada automaticamente
      setTimeout(() => {
        loadStartupDetails(data.id);
      }, 500);
    } catch (error) {
      console.error("Erro ao criar startup:", error);
      showToast("Erro ao criar startup", "error");
    }
  };

  const handleFollowStartup = async (startupId) => {
    try {
      // Verificar se já segue
      const { data: existing } = await supabase
        .from("startup_followers")
        .select("id")
        .eq("startup_id", startupId)
        .eq("user_id", user.id)
        .single();

      if (existing) {
        // Deixar de seguir
        const { error } = await supabase
          .from("startup_followers")
          .delete()
          .eq("startup_id", startupId)
          .eq("user_id", user.id);

        if (error) throw error;
        showToast("Você deixou de seguir esta startup", "info");
      } else {
        // Seguir
        const { error } = await supabase
          .from("startup_followers")
          .insert([{
            startup_id: startupId,
            user_id: user.id
          }]);

        if (error) throw error;
        showToast("Você agora segue esta startup!", "success");
      }

      loadData();
    } catch (error) {
      console.error("Erro ao seguir/deixar de seguir:", error);
      showToast("Erro ao processar ação", "error");
    }
  };

  const handlePostUpdate = async (e) => {
    e.preventDefault();

    if (!selectedStartup || !selectedStartup.id) {
      showToast("Erro: Startup não selecionada", "error");
      setShowUpdateForm(false);
      return;
    }

    if (!updateData.title || !updateData.content) {
      showToast("Preencha título e conteúdo", "error");
      return;
    }

    try {
      const { data, error } = await supabase
        .from("startup_updates")
        .insert([{
          startup_id: selectedStartup.id,
          title: updateData.title,
          content: updateData.content,
          author_id: user.id
        }])
        .select();

      if (error) {
        console.error("Erro detalhado:", error);
        throw error;
      }

      showToast("Atualização publicada!", "success");
      setShowUpdateForm(false);
      setUpdateData({ title: "", content: "" });
      loadStartupDetails(selectedStartup.id);
    } catch (error) {
      console.error("Erro ao postar atualização:", error);
      showToast("Erro ao postar atualização", "error");
    }
  };

  const handlePostJob = async (e) => {
    e.preventDefault();

    if (!selectedStartup || !selectedStartup.id) {
      showToast("Erro: Startup não selecionada", "error");
      setShowJobForm(false);
      return;
    }

    if (!jobData.title || !jobData.description) {
      showToast("Preencha título e descrição da vaga", "error");
      return;
    }

    try {
      const { data, error } = await supabase
        .from("startup_jobs")
        .insert([{
          startup_id: selectedStartup.id,
          title: jobData.title,
          description: jobData.description,
          requirements: jobData.requirements || null,
          location: jobData.location || null,
          type: jobData.type,
          salary_range: jobData.salary_range || null,
          posted_by: user.id,
          is_active: true
        }])
        .select();

      if (error) {
        console.error("Erro detalhado:", error);
        throw error;
      }

      showToast("Vaga publicada!", "success");
      setShowJobForm(false);
      setJobData({
        title: "",
        description: "",
        requirements: "",
        location: "",
        type: "full-time",
        salary_range: ""
      });
      loadStartupDetails(selectedStartup.id);
    } catch (error) {
      console.error("Erro ao publicar vaga:", error);
      showToast("Erro ao publicar vaga", "error");
    }
  };

  const handleDeleteStartup = async () => {
    if (!selectedStartup || !selectedStartup.id) {
      showToast("Erro: Startup não selecionada", "error");
      return;
    }

    // Verificar se é o dono
    if (selectedStartup.owner_id !== user.id) {
      showToast("Você não tem permissão para deletar esta startup", "error");
      return;
    }

    const startupId = selectedStartup.id;
    const startupName = selectedStartup.name;

    setConfirmModal({
      message: `Tem certeza que deseja deletar a startup "${startupName}"? Esta ação não pode ser desfeita.`,
      onConfirm: async () => {
        try {
          // Deletar apenas a startup específica E que pertence ao usuário
          const { error } = await supabase
            .from("startups")
            .delete()
            .eq("id", startupId)
            .eq("owner_id", user.id);

          if (error) {
            console.error("Erro ao deletar:", error);
            throw error;
          }

          // Fechar modal e limpar estado ANTES de recarregar
          setSelectedStartup(null);
          setConfirmModal(null);
          
          // Recarregar lista
          await loadStartups();
          
          showToast("Startup deletada com sucesso!", "success");
        } catch (error) {
          console.error("Erro ao deletar startup:", error);
          showToast("Erro ao deletar startup", "error");
          setConfirmModal(null);
        }
      }
    });
  };

  const loadStartupDetails = async (startupId) => {
    try {
      const { data: startup, error: startupError } = await supabase
        .from("startups")
        .select(`
          *,
          owner:users!startups_owner_id_fkey(id, nome),
          followers:startup_followers(count)
        `)
        .eq("id", startupId)
        .single();

      if (startupError) throw startupError;

      // Buscar lista de seguidores com detalhes
      const { data: followersList, error: followersError } = await supabase
        .from("startup_followers")
        .select(`
          user:users(id, nome, avatar)
        `)
        .eq("startup_id", startupId)
        .limit(20);

      startup.followersList = followersList?.map(f => f.user) || [];

      const { data: updates, error: updatesError } = await supabase
        .from("startup_updates")
        .select(`
          *,
          author:users!startup_updates_author_id_fkey(id, nome)
        `)
        .eq("startup_id", startupId)
        .order("created_at", { ascending: false });

      const { data: jobs, error: jobsError } = await supabase
        .from("startup_jobs")
        .select("*")
        .eq("startup_id", startupId)
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      setSelectedStartup({
        ...startup,
        updates: updates || [],
        jobs: jobs || []
      });

      // Carregar curtidas e comentários para cada atualização
      if (updates && updates.length > 0) {
        updates.forEach(update => {
          loadUpdateInteractions(update.id);
        });
      }
    } catch (error) {
      console.error("Erro ao carregar detalhes:", error);
      showToast("Erro ao carregar detalhes da startup", "error");
    }
  };

  const isFollowing = (startupId) => {
    return followedStartups.some(s => s.id === startupId);
  };

  const isOwner = (startup) => {
    return startup.owner_id === user.id;
  };

  // Carregar curtidas e comentários de uma atualização
  const loadUpdateInteractions = async (updateId) => {
    try {
      // Carregar curtidas
      const { data: likes, error: likesError } = await supabase
        .from("startup_update_likes")
        .select("id, user_id")
        .eq("update_id", updateId);

      if (!likesError && likes) {
        setUpdateLikes(prev => ({
          ...prev,
          [updateId]: {
            count: likes.length,
            userLiked: likes.some(l => l.user_id === user.id)
          }
        }));
      }

      // Carregar comentários
      const { data: comments, error: commentsError } = await supabase
        .from("startup_update_comments")
        .select(`
          id,
          content,
          created_at,
          user:users(id, nome, avatar)
        `)
        .eq("update_id", updateId)
        .order("created_at", { ascending: true });

      if (!commentsError && comments) {
        setUpdateComments(prev => ({
          ...prev,
          [updateId]: comments
        }));
      }
    } catch (error) {
      console.error("Erro ao carregar interações:", error);
    }
  };

  // Curtir/descurtir atualização
  const handleLikeUpdate = async (updateId) => {
    try {
      const currentLike = updateLikes[updateId];
      
      if (currentLike?.userLiked) {
        // Remover curtida
        await supabase
          .from("startup_update_likes")
          .delete()
          .eq("update_id", updateId)
          .eq("user_id", user.id);

        setUpdateLikes(prev => ({
          ...prev,
          [updateId]: {
            count: (prev[updateId]?.count || 1) - 1,
            userLiked: false
          }
        }));
      } else {
        // Adicionar curtida
        await supabase
          .from("startup_update_likes")
          .insert([{
            update_id: updateId,
            user_id: user.id
          }]);

        setUpdateLikes(prev => ({
          ...prev,
          [updateId]: {
            count: (prev[updateId]?.count || 0) + 1,
            userLiked: true
          }
        }));
      }
    } catch (error) {
      console.error("Erro ao curtir:", error);
      showToast("Erro ao curtir", "error");
    }
  };

  // Adicionar comentário
  const handleAddComment = async (updateId) => {
    const text = commentText[updateId]?.trim();
    if (!text) return;

    try {
      const { data, error } = await supabase
        .from("startup_update_comments")
        .insert([{
          update_id: updateId,
          user_id: user.id,
          content: text
        }])
        .select(`
          id,
          content,
          created_at,
          user:users(id, nome, avatar)
        `)
        .single();

      if (error) throw error;

      setUpdateComments(prev => ({
        ...prev,
        [updateId]: [...(prev[updateId] || []), data]
      }));

      setCommentText(prev => ({
        ...prev,
        [updateId]: ""
      }));

      showToast("Comentário adicionado!", "success");
    } catch (error) {
      console.error("Erro ao comentar:", error);
      showToast("Erro ao comentar", "error");
    }
  };

  // Deletar comentário
  const handleDeleteComment = async (updateId, commentId) => {
    try {
      const { error } = await supabase
        .from("startup_update_comments")
        .delete()
        .eq("id", commentId);

      if (error) throw error;

      setUpdateComments(prev => ({
        ...prev,
        [updateId]: prev[updateId].filter(c => c.id !== commentId)
      }));

      showToast("Comentário removido", "success");
    } catch (error) {
      console.error("Erro ao deletar comentário:", error);
      showToast("Erro ao deletar comentário", "error");
    }
  };

  // ========== FUNÇÕES DA COMUNIDADE ==========
  const loadCommunityPosts = async (startupId) => {
    if (!startupId) return;
    
    try {
      setLoadingCommunity(true);
      
      // Buscar posts
      const { data: posts, error: postsError } = await supabase
        .from("startup_community_posts")
        .select("*")
        .eq("startup_id", startupId)
        .order("created_at", { ascending: false });

      if (postsError) throw postsError;

      // Buscar dados dos autores separadamente
      if (posts && posts.length > 0) {
        const authorIds = [...new Set(posts.map(p => p.author_id))];
        const { data: authors, error: authorsError } = await supabase
          .from("users")
          .select("id, nome, avatar, verified")
          .in("id", authorIds);

        if (!authorsError && authors) {
          // Mapear autores aos posts
          const authorsMap = {};
          authors.forEach(author => {
            authorsMap[author.id] = author;
          });

          const postsWithAuthors = posts.map(post => ({
            ...post,
            author: authorsMap[post.author_id] || null
          }));

          setCommunityPosts(postsWithAuthors);
        } else {
          setCommunityPosts(posts);
        }

        // Carregar reações de todos os posts
        const postIds = posts.map(p => p.id);
        const { data: reactions, error: reactionsError } = await supabase
          .from("startup_community_reactions")
          .select("*")
          .in("post_id", postIds);

        if (!reactionsError && reactions) {
          // Organizar reações por post
          const reactionsByPost = {};
          reactions.forEach(reaction => {
            if (!reactionsByPost[reaction.post_id]) {
              reactionsByPost[reaction.post_id] = [];
            }
            reactionsByPost[reaction.post_id].push(reaction);
          });
          setCommunityReactions(reactionsByPost);
        }
      } else {
        setCommunityPosts([]);
        setCommunityReactions({});
      }
    } catch (error) {
      console.error("Erro ao carregar posts da comunidade:", error);
      showToast("Erro ao carregar comunidade", "error");
      setCommunityPosts([]);
    } finally {
      setLoadingCommunity(false);
    }
  };

  const handlePostToCommunity = async () => {
    const startup = selectedStartup || communityStartup;
    
    if (!communityPostText.trim() || !startup) {
      showToast("Digite algo para postar!", "warning");
      return;
    }

    try {
      const { data, error } = await supabase
        .from("startup_community_posts")
        .insert([{
          startup_id: startup.id,
          author_id: user.id,
          content: communityPostText.trim()
        }])
        .select(`
          *,
          author:users(id, nome, avatar, verified)
        `);

      if (error) throw error;

      // Evitar duplicatas - verificar se o post já existe
      setCommunityPosts(prev => {
        const postExists = prev.some(p => p.id === data[0].id);
        if (postExists) return prev;
        return [data[0], ...prev];
      });
      setCommunityPostText("");
      showToast("Post publicado na comunidade! 🎉", "success");
    } catch (error) {
      console.error("Erro ao postar na comunidade:", error);
      showToast("Erro ao publicar post", "error");
    }
  };

  const handleDeleteCommunityPost = async (postId) => {
    try {
      const { error } = await supabase
        .from("startup_community_posts")
        .delete()
        .eq("id", postId);

      if (error) throw error;

      setCommunityPosts(prev => prev.filter(p => p.id !== postId));
      setConfirmModal(null); // Fechar o modal
      showToast("Post removido", "success");
    } catch (error) {
      console.error("Erro ao deletar post:", error);
      setConfirmModal(null); // Fechar o modal mesmo em erro
      showToast("Erro ao deletar post", "error");
    }
  };

  // ========== COMENTÁRIOS NA COMUNIDADE (ABA COMMUNITY-DETAIL) ==========
  const handleComment = async (postId) => {
    const text = communityCommentText[postId]?.trim();
    if (!text) return;

    try {
      const { data, error } = await supabase
        .from("startup_community_comments")
        .insert([{
          post_id: postId,
          author_id: user.id,
          content: text
        }])
        .select();

      if (error) throw error;

      const { data: authorData } = await supabase
        .from("users")
        .select("id, nome, avatar, verified")
        .eq("id", user.id)
        .single();

      const newComment = {
        ...data[0],
        author: authorData
      };

      // Verificar se o comentário já existe antes de adicionar
      setCommunityComments(prev => {
        const existingComments = prev[postId] || [];
        const commentExists = existingComments.some(c => c.id === newComment.id);
        
        if (commentExists) {
          return prev;
        }
        
        return {
          ...prev,
          [postId]: [...existingComments, newComment]
        };
      });

      setCommunityCommentText(prev => ({
        ...prev,
        [postId]: ""
      }));

      showToast("Comentário publicado!", "success");
    } catch (error) {
      console.error("Erro ao comentar:", error);
      showToast("Erro ao comentar", "error");
    }
  };

  const handleReply = async (postId, parentCommentId) => {
    const commentKey = `${postId}_reply_${parentCommentId}`;
    const text = communityCommentText[commentKey]?.trim();
    if (!text) return;

    try {
      const { data, error } = await supabase
        .from("startup_community_comments")
        .insert([{
          post_id: postId,
          author_id: user.id,
          parent_comment_id: parentCommentId,
          content: text
        }])
        .select();

      if (error) throw error;

      const { data: authorData } = await supabase
        .from("users")
        .select("id, nome, avatar, verified")
        .eq("id", user.id)
        .single();

      const newComment = {
        ...data[0],
        author: authorData
      };

      // Verificar se o comentário já existe antes de adicionar
      setCommunityComments(prev => {
        const existingComments = prev[postId] || [];
        const commentExists = existingComments.some(c => c.id === newComment.id);
        
        if (commentExists) {
          return prev;
        }
        
        return {
          ...prev,
          [postId]: [...existingComments, newComment]
        };
      });

      setCommunityCommentText(prev => ({
        ...prev,
        [commentKey]: ""
      }));

      // Fechar campo de resposta
      setReplyingTo(prev => ({
        ...prev,
        [`${postId}_${parentCommentId}`]: false
      }));

      showToast("Resposta publicada!", "success");
    } catch (error) {
      console.error("Erro ao responder:", error);
      showToast("Erro ao comentar", "error");
    }
  };

  const handleDeleteCommunityComment = async (commentId) => {
    try {
      const { error } = await supabase
        .from("startup_community_comments")
        .delete()
        .eq("id", commentId);

      if (error) throw error;

      // Remover o comentário/resposta do estado
      setCommunityPosts(prev => prev.map(post => ({
        ...post,
        comments: post.comments?.filter(c => c.id !== commentId) || []
      })));

      setConfirmModal(null);
      showToast("Comentário removido", "success");
    } catch (error) {
      console.error("Erro ao deletar comentário:", error);
      setConfirmModal(null);
      showToast("Erro ao deletar comentário", "error");
    }
  };

  // ========== REAÇÕES NA COMUNIDADE ==========
  const loadCommunityReactions = async (postId) => {
    try {
      const { data, error } = await supabase
        .from("startup_community_reactions")
        .select("*")
        .eq("post_id", postId);

      if (error) throw error;

      setCommunityReactions(prev => ({
        ...prev,
        [postId]: data || []
      }));
    } catch (error) {
      console.error("Erro ao carregar reações:", error);
    }
  };

  const handleCommunityReaction = async (postId, reactionType) => {
    try {
      const reactions = communityReactions[postId] || [];
      const existingReaction = reactions.find(r => r.user_id === user.id);

      if (existingReaction) {
        if (existingReaction.reaction_type === reactionType) {
          // Remover reação
          const { error } = await supabase
            .from("startup_community_reactions")
            .delete()
            .eq("id", existingReaction.id);

          if (error) throw error;

          setCommunityReactions(prev => ({
            ...prev,
            [postId]: reactions.filter(r => r.id !== existingReaction.id)
          }));
        } else {
          // Atualizar reação
          const { error } = await supabase
            .from("startup_community_reactions")
            .update({ reaction_type: reactionType })
            .eq("id", existingReaction.id);

          if (error) throw error;

          setCommunityReactions(prev => ({
            ...prev,
            [postId]: reactions.map(r => 
              r.id === existingReaction.id ? { ...r, reaction_type: reactionType } : r
            )
          }));
        }
      } else {
        // Adicionar nova reação
        const { data, error } = await supabase
          .from("startup_community_reactions")
          .insert([{
            post_id: postId,
            user_id: user.id,
            reaction_type: reactionType
          }])
          .select();

        if (error) throw error;

        setCommunityReactions(prev => ({
          ...prev,
          [postId]: [...reactions, data[0]]
        }));
      }

      setShowReactionPicker(null);
    } catch (error) {
      console.error("Erro ao reagir:", error);
      showToast("Erro ao reagir", "error");
    }
  };

  // Toggle para abrir/fechar o seletor de reações
  const toggleReactionPicker = (postId) => {
    setShowReactionPicker(prev => prev === postId ? null : postId);
  };

  // Toggle para abrir/fechar menus de posts/comentários
  const togglePostMenu = (menuId) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  // ========== COMENTÁRIOS NA COMUNIDADE ==========
  const loadCommunityComments = async (postId) => {
    try {
      const { data: comments, error: commentsError } = await supabase
        .from("startup_community_comments")
        .select("*")
        .eq("post_id", postId)
        .order("created_at", { ascending: true });

      if (commentsError) throw commentsError;

      if (comments && comments.length > 0) {
        const authorIds = [...new Set(comments.map(c => c.author_id))];
        const { data: authors, error: authorsError } = await supabase
          .from("users")
          .select("id, nome, avatar, verified")
          .in("id", authorIds);

        if (!authorsError && authors) {
          const authorsMap = {};
          authors.forEach(author => {
            authorsMap[author.id] = author;
          });

          const commentsWithAuthors = comments.map(comment => ({
            ...comment,
            author: authorsMap[comment.author_id] || null
          }));

          // Remover duplicados por ID antes de setar
          const uniqueComments = commentsWithAuthors.filter((comment, index, self) =>
            index === self.findIndex((c) => c.id === comment.id)
          );

          setCommunityComments(prev => ({
            ...prev,
            [postId]: uniqueComments
          }));
        } else {
          // Remover duplicados por ID
          const uniqueComments = comments.filter((comment, index, self) =>
            index === self.findIndex((c) => c.id === comment.id)
          );
          
          setCommunityComments(prev => ({
            ...prev,
            [postId]: uniqueComments
          }));
        }
      } else {
        setCommunityComments(prev => ({
          ...prev,
          [postId]: []
        }));
      }
    } catch (error) {
      console.error("Erro ao carregar comentários:", error);
    }
  };

  const handleCommunityComment = async (postId, parentCommentId = null) => {
    const commentKey = parentCommentId ? `${postId}_reply_${parentCommentId}` : postId;
    const text = communityCommentText[commentKey]?.trim();
    if (!text) return;

    try {
      const { data, error } = await supabase
        .from("startup_community_comments")
        .insert([{
          post_id: postId,
          author_id: user.id,
          parent_comment_id: parentCommentId,
          content: text
        }])
        .select();

      if (error) throw error;

      const { data: authorData } = await supabase
        .from("users")
        .select("id, nome, avatar, verified")
        .eq("id", user.id)
        .single();

      const newComment = {
        ...data[0],
        author: authorData
      };

      // Verificar se o comentário já existe antes de adicionar
      setCommunityComments(prev => {
        const existingComments = prev[postId] || [];
        const commentExists = existingComments.some(c => c.id === newComment.id);
        
        if (commentExists) {
          return prev;
        }
        
        return {
          ...prev,
          [postId]: [...existingComments, newComment]
        };
      });

      setCommunityCommentText(prev => ({
        ...prev,
        [commentKey]: ""
      }));

      // Fechar campo de resposta
      if (parentCommentId) {
        setReplyingTo(prev => ({
          ...prev,
          [`${postId}_${parentCommentId}`]: false
        }));
      }

      showToast(parentCommentId ? "Resposta publicada!" : "Comentário publicado!", "success");
    } catch (error) {
      console.error("Erro ao comentar:", error);
      showToast("Erro ao comentar", "error");
    }
  };

  // ========== FEED GLOBAL DE COMUNIDADES ==========
  const loadAllCommunityPosts = async () => {
    if (followedStartups.length === 0) {
      console.log("Nenhuma startup seguida");
      return;
    }
    
    try {
      setLoadingAllCommunity(true);
      
      // Pegar IDs das startups seguidas
      const startupIds = followedStartups.map(s => s.startup?.id || s.id).filter(Boolean);
      console.log("Buscando posts de startups:", startupIds);
      
      // Buscar todos os posts dessas startups
      const { data: posts, error: postsError } = await supabase
        .from("startup_community_posts")
        .select("*")
        .in("startup_id", startupIds)
        .order("created_at", { ascending: false })
        .limit(50);

      if (postsError) throw postsError;
      
      console.log("Posts encontrados:", posts);

      if (posts && posts.length > 0) {
        // Buscar dados dos autores
        const authorIds = [...new Set(posts.map(p => p.author_id))];
        console.log("Buscando autores:", authorIds);
        
        const { data: authors, error: authorsError } = await supabase
          .from("users")
          .select("id, nome, avatar, verified")
          .in("id", authorIds);

        console.log("Autores encontrados:", authors);

        // Buscar dados das startups (usando os IDs únicos dos posts)
        const postStartupIds = [...new Set(posts.map(p => p.startup_id))];
        console.log("IDs de startups dos posts:", postStartupIds);
        
        const { data: startups, error: startupsError } = await supabase
          .from("startups")
          .select("id, name, logo_url, primary_color")
          .in("id", postStartupIds);
          
        console.log("Startups encontradas:", startups);
        if (startupsError) console.error("Erro ao buscar startups:", startupsError);

        if (!authorsError && authors && !startupsError && startups) {
          const authorsMap = {};
          authors.forEach(author => {
            authorsMap[author.id] = author;
          });

          const startupsMap = {};
          startups.forEach(startup => {
            startupsMap[startup.id] = startup;
          });

          const postsWithData = posts.map(post => ({
            ...post,
            author: authorsMap[post.author_id] || null,
            startup: startupsMap[post.startup_id] || null
          }));

          console.log("Posts com dados completos:", postsWithData);
          setAllCommunityPosts(postsWithData);
          
          // Carregar reações e comentários
          postsWithData.forEach(post => {
            loadCommunityReactions(post.id);
            loadCommunityComments(post.id);
          });
        } else {
          console.log("Erro ao buscar autores ou startups");
          setAllCommunityPosts(posts);
        }
      } else {
        console.log("Nenhum post encontrado");
        setAllCommunityPosts([]);
      }
    } catch (error) {
      console.error("Erro ao carregar feed de comunidades:", error);
      showToast("Erro ao carregar comunidades", "error");
      setAllCommunityPosts([]);
    } finally {
      setLoadingAllCommunity(false);
    }
  };

  const renderStartupCard = (startup) => (
    <div key={startup.id} style={{ 
      marginBottom: "20px",
      background: "#fff",
      border: "1px solid #D9E1EE",
      borderRadius: "8px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      transition: "all 0.2s ease"
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
      e.currentTarget.style.transform = "translateY(-2px)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.05)";
      e.currentTarget.style.transform = "translateY(0)";
    }}
    >
      {/* Header da startup com cor */}
      <div style={{
        background: "linear-gradient(135deg, #1B4398 0%, #2557B5 100%)",
        padding: "16px 20px",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        gap: "12px"
      }}>
        {startup.logo_url ? (
          <img
            src={startup.logo_url}
            alt={startup.name}
            style={{
              width: "50px",
              height: "50px",
              borderRadius: "6px",
              objectFit: "contain",
              border: "2px solid rgba(255,255,255,0.3)",
              background: "white",
              padding: "3px"
            }}
          />
        ) : (
          <div style={{
            width: "50px",
            height: "50px",
            borderRadius: "6px",
            background: "rgba(255,255,255,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.4em",
            fontWeight: "bold",
            border: "2px solid rgba(255,255,255,0.3)"
          }}>
            {startup.name.charAt(0).toUpperCase()}
          </div>
        )}
        <div style={{ flex: 1 }}>
          <h3 style={{ 
            margin: "0 0 4px 0", 
            fontSize: "1.2em",
            fontWeight: "600"
          }}>
            {startup.name}
          </h3>
          <div style={{ fontSize: "0.85em", opacity: 0.9 }}>
            Por {startup.owner?.nome || "Proprietário"}
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      <div style={{ padding: "20px" }}>
        <p style={{ 
          margin: "0 0 15px 0", 
          color: "#555", 
          fontSize: "0.95em",
          lineHeight: "1.5"
        }}>
          {startup.description}
        </p>
        
        {/* Tags */}
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "15px" }}>
          {startup.category && (
            <span style={{
              display: "inline-block",
              padding: "6px 14px",
              background: "#E8F0FE",
              color: "#1B4398",
              borderRadius: "20px",
              fontSize: "0.8em",
              fontWeight: "600"
            }}>
              {startup.category}
            </span>
          )}
          {startup.location && (
            <span style={{
              display: "inline-block",
              padding: "6px 14px",
              background: "#F0F2F5",
              color: "#666",
              borderRadius: "20px",
              fontSize: "0.8em",
              fontWeight: "500"
            }}>
              {startup.location}
            </span>
          )}
        </div>

        {/* Stats */}
        <div style={{ 
          display: "flex", 
          gap: "25px", 
          fontSize: "0.9em", 
          color: "#666",
          padding: "15px 0",
          borderTop: "1px solid #E8F0FE",
          borderBottom: "1px solid #E8F0FE",
          marginBottom: "15px"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <span style={{ fontWeight: "700", color: "#1B4398", fontSize: "1.1em" }}>
              {startup.followers?.[0]?.count || 0}
            </span>
            <span>seguidores</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <span style={{ fontWeight: "700", color: "#1B4398", fontSize: "1.1em" }}>
              {startup.updates?.[0]?.count || 0}
            </span>
            <span>atualizações</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <span style={{ fontWeight: "700", color: "#1B4398", fontSize: "1.1em" }}>
              {startup.jobs?.[0]?.count || 0}
            </span>
            <span>vagas</span>
          </div>
        </div>

        {/* Botões */}
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button
            style={{ 
              flex: "1",
              minWidth: "120px",
              padding: "10px 20px",
              background: "#fff",
              color: "#1B4398",
              border: "2px solid #1B4398",
              borderRadius: "8px",
              fontWeight: "600",
              fontSize: "0.9em",
              cursor: "pointer",
              transition: "all 0.3s ease",
              boxShadow: "0 2px 8px rgba(27, 67, 152, 0.1)"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#1B4398";
              e.currentTarget.style.color = "#fff";
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(27, 67, 152, 0.25)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#fff";
              e.currentTarget.style.color = "#1B4398";
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(27, 67, 152, 0.1)";
            }}
            onClick={() => loadStartupDetails(startup.id)}
          >
            Ver Detalhes
          </button>
          {!isOwner(startup) && (
            <button
              style={{ 
                flex: "1",
                minWidth: "100px",
                padding: "10px 20px",
                background: isFollowing(startup.id) ? "#fff" : "#D4418E",
                color: isFollowing(startup.id) ? "#6C757D" : "#fff",
                border: isFollowing(startup.id) ? "2px solid #6C757D" : "2px solid #D4418E",
                borderRadius: "8px",
                fontWeight: "600",
                fontSize: "0.9em",
                cursor: "pointer",
                transition: "all 0.3s ease",
                boxShadow: isFollowing(startup.id) 
                  ? "0 2px 8px rgba(108, 117, 125, 0.1)"
                  : "0 2px 8px rgba(212, 65, 142, 0.1)"
              }}
              onMouseEnter={(e) => {
                if (isFollowing(startup.id)) {
                  e.currentTarget.style.background = "#6C757D";
                  e.currentTarget.style.color = "#fff";
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(108, 117, 125, 0.25)";
                } else {
                  e.currentTarget.style.background = "#fff";
                  e.currentTarget.style.color = "#D4418E";
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(212, 65, 142, 0.25)";
                }
              }}
              onMouseLeave={(e) => {
                if (isFollowing(startup.id)) {
                  e.currentTarget.style.background = "#fff";
                  e.currentTarget.style.color = "#6C757D";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(108, 117, 125, 0.1)";
                } else {
                  e.currentTarget.style.background = "#D4418E";
                  e.currentTarget.style.color = "#fff";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(212, 65, 142, 0.1)";
                }
              }}
              onClick={() => handleFollowStartup(startup.id)}
            >
              {isFollowing(startup.id) ? "Seguindo" : "Seguir"}
            </button>
          )}
          {startup.website && (
            <a
              href={startup.website}
              target="_blank"
              rel="noopener noreferrer"
              className="orkut-btn"
              style={{ 
                flex: "1",
                minWidth: "80px",
                textDecoration: "none",
                background: "#fff",
                color: "#28A745",
                border: "2px solid #28A745",
                fontWeight: "600",
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#28A745";
                e.currentTarget.style.color = "#fff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#fff";
                e.currentTarget.style.color = "#28A745";
              }}
            >
              Site
            </a>
          )}
        </div>
      </div>
    </div>
  );

  const renderStartupDetails = () => {
    if (!selectedStartup) return null;

    const isMobile = window.innerWidth <= 768;

    return (
      <div className="orkut-modal-bg" onClick={() => setSelectedStartup(null)}>
        <div 
          className={isMobile ? "orkut-modal-mobile-fullscreen" : "orkut-modal"} 
          onClick={(e) => e.stopPropagation()} 
          style={isMobile ? {
            width: "100%",
            height: "100%",
            maxWidth: "100%",
            maxHeight: "100%",
            borderRadius: 0,
            margin: 0,
            overflowY: "auto"
          } : { 
            maxWidth: "800px", 
            maxHeight: "90vh", 
            overflowY: "auto"
          }}
        >
          <button
            className="orkut-modal-close"
            onClick={() => setSelectedStartup(null)}
            style={{
              position: "absolute",
              top: isMobile ? "10px" : "15px",
              right: isMobile ? "10px" : "15px",
              zIndex: 10
            }}
          >
            ×
          </button>

          {/* Header */}
          <div style={{ 
            marginBottom: "0",
            background: "#1B4398",
            padding: isMobile ? "20px 15px" : "20px",
            borderRadius: isMobile ? 0 : "8px 8px 0 0",
            color: "#fff"
          }}>
            {/* Layout Mobile - Organizado */}
            {isMobile ? (
              <div>
                {/* Logo e Nome */}
                <div style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "15px" }}>
                  {selectedStartup.logo_url ? (
                    <div style={{
                      width: "70px",
                      height: "70px",
                      borderRadius: "8px",
                      border: "3px solid rgba(255,255,255,0.3)",
                      flexShrink: 0,
                      background: "white",
                      padding: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden"
                    }}>
                      <img
                        src={selectedStartup.logo_url}
                        alt={selectedStartup.name}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain"
                        }}
                      />
                    </div>
                  ) : (
                    <div style={{
                      width: "70px",
                      height: "70px",
                      borderRadius: "8px",
                      background: "rgba(255,255,255,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.5em",
                      fontWeight: "bold",
                      border: "3px solid rgba(255,255,255,0.3)",
                      flexShrink: 0
                    }}>
                      {selectedStartup.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h2 style={{ 
                      margin: "0 0 8px 0", 
                      color: "#fff", 
                      fontSize: "1.3em",
                      fontWeight: "700",
                      wordBreak: "break-word"
                    }}>
                      {selectedStartup.name}
                    </h2>
                  </div>
                </div>

                {/* Descrição */}
                <p style={{ 
                  margin: "0 0 12px 0", 
                  color: "rgba(255,255,255,0.95)", 
                  lineHeight: "1.6",
                  fontSize: "0.95em"
                }}>
                  {selectedStartup.description}
                </p>

                {/* Seguidores */}
                <div style={{ marginBottom: "12px" }}>
                  <div style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: "4px",
                    marginBottom: "8px",
                    justifyContent: "flex-start"
                  }}>
                    <svg 
                      viewBox="0 0 24 24" 
                      fill="white" 
                      width="11" 
                      height="11" 
                      style={{ 
                        minWidth: "11px",
                        minHeight: "11px",
                        maxWidth: "11px",
                        maxHeight: "11px",
                        flexShrink: 0,
                        display: "block"
                      }}
                    >
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>
                    <span style={{ fontSize: "0.8em", opacity: 0.9, fontWeight: "500" }}>
                      {selectedStartup.followers?.[0]?.count || 0} seguidores
                    </span>
                  </div>
                  <div style={{ 
                    display: "flex", 
                    gap: "6px", 
                    overflowX: "auto",
                    paddingBottom: "4px"
                  }}>
                    {(() => {
                      const realFollowers = selectedStartup.followersList || [];
                      const totalToShow = 10;
                      const placeholders = Array(Math.max(0, totalToShow - realFollowers.length)).fill(null);
                      const allAvatars = [...realFollowers.slice(0, totalToShow), ...placeholders].slice(0, totalToShow);
                      
                      return allAvatars.map((follower, idx) => (
                        <div key={idx} style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "6px",
                          background: follower?.avatar ? "#fff" : "rgba(255,255,255,0.15)",
                          border: "2px solid rgba(255,255,255,0.3)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          overflow: "hidden"
                        }}>
                          {follower?.avatar ? (
                            <img 
                              src={follower.avatar} 
                              alt={follower.nome}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover"
                              }}
                            />
                          ) : follower ? (
                            <span style={{
                              color: "white",
                              fontSize: "0.7em",
                              fontWeight: "bold"
                            }}>
                              {follower.nome?.charAt(0)?.toUpperCase()}
                            </span>
                          ) : (
                            <svg viewBox="0 0 24 24" fill="rgba(255,255,255,0.4)" width="16" height="16">
                              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                            </svg>
                          )}
                        </div>
                      ));
                    })()}
                  </div>
                </div>

                {/* Tags */}
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                  {selectedStartup.category && (
                    <span style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: "5px 10px",
                      background: "rgba(255,255,255,0.2)",
                      borderRadius: "20px",
                      fontSize: "0.75em",
                      fontWeight: "500"
                    }}>
                      <svg viewBox="0 0 24 24" fill="white" width="13" height="13">
                        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                      </svg>
                      {selectedStartup.category}
                    </span>
                  )}
                  {selectedStartup.location && (
                    <span style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: "5px 10px",
                      background: "rgba(255,255,255,0.2)",
                      borderRadius: "20px",
                      fontSize: "0.75em",
                      fontWeight: "500"
                    }}>
                      <svg viewBox="0 0 24 24" fill="white" width="13" height="13">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                      </svg>
                      {selectedStartup.location}
                    </span>
                  )}
                  {selectedStartup.website && (
                    <a 
                      href={selectedStartup.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: "5px 10px",
                        background: "rgba(255,255,255,0.2)",
                        borderRadius: "20px",
                        fontSize: "0.75em",
                        fontWeight: "500",
                        color: "white",
                        textDecoration: "none"
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="white" width="13" height="13">
                        <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
                      </svg>
                      website
                    </a>
                  )}
                </div>
              </div>
            ) : (
              /* Layout Desktop - Original */
              <div style={{ display: "flex", gap: "15px", alignItems: "flex-start" }}>
                {selectedStartup.logo_url ? (
                  <div style={{
                    width: "100px",
                    height: "100px",
                    borderRadius: "8px",
                    border: "3px solid rgba(255,255,255,0.3)",
                    flexShrink: 0,
                    background: "white",
                    padding: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden"
                  }}>
                    <img
                      src={selectedStartup.logo_url}
                      alt={selectedStartup.name}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain"
                      }}
                    />
                  </div>
                ) : (
                  <div style={{
                    width: "100px",
                    height: "100px",
                    borderRadius: "8px",
                    background: "rgba(255,255,255,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "2em",
                    fontWeight: "bold",
                    border: "3px solid rgba(255,255,255,0.3)",
                    flexShrink: 0
                  }}>
                    {selectedStartup.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h2 style={{ 
                    margin: "0 0 10px 0", 
                    color: "#fff", 
                    fontSize: "1.5em",
                    fontWeight: "700"
                  }}>
                    {selectedStartup.name}
                  </h2>
                  <p style={{ 
                    margin: "0 0 14px 0", 
                    color: "rgba(255,255,255,0.95)", 
                    lineHeight: "1.5",
                    fontSize: "1em"
                  }}>
                    {selectedStartup.description}
                  </p>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
                    {selectedStartup.category && (
                      <span style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        padding: "4px 12px",
                        background: "rgba(255,255,255,0.2)",
                        borderRadius: "20px",
                        fontSize: "0.75em",
                        fontWeight: "500"
                      }}>
                        <svg viewBox="0 0 24 24" fill="white" width="14" height="14">
                          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                        </svg>
                        {selectedStartup.category}
                      </span>
                    )}
                    {selectedStartup.location && (
                      <span style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        padding: "4px 12px",
                        background: "rgba(255,255,255,0.2)",
                        borderRadius: "20px",
                        fontSize: "0.75em",
                        fontWeight: "500"
                      }}>
                        <svg viewBox="0 0 24 24" fill="white" width="14" height="14">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                        </svg>
                        {selectedStartup.location}
                      </span>
                    )}
                    {selectedStartup.website && (
                      <a 
                        href={selectedStartup.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                          padding: "4px 12px",
                          background: "rgba(255,255,255,0.2)",
                          borderRadius: "20px",
                          fontSize: "0.75em",
                          fontWeight: "500",
                          color: "white",
                          textDecoration: "none"
                        }}
                      >
                        <svg viewBox="0 0 24 24" fill="white" width="14" height="14">
                          <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
                        </svg>
                        website
                      </a>
                    )}
                  </div>
                  
                  {/* Seguidores Desktop */}
                  <div style={{ marginTop: "12px" }}>
                    <div style={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: "5px",
                      marginBottom: "10px"
                    }}>
                      <svg 
                        viewBox="0 0 24 24" 
                        fill="white" 
                        width="13" 
                        height="13" 
                        style={{ 
                          minWidth: "13px",
                          minHeight: "13px",
                          maxWidth: "13px",
                          maxHeight: "13px",
                          flexShrink: 0,
                          display: "block"
                        }}
                      >
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                      </svg>
                      <span style={{ fontSize: "0.85em", opacity: 0.95, fontWeight: "500" }}>
                        {selectedStartup.followers?.[0]?.count || 0} seguidores
                      </span>
                    </div>
                      <div style={{ 
                        display: "flex", 
                        gap: "8px", 
                        overflowX: "auto",
                        paddingBottom: "6px"
                      }}>
                        {(() => {
                          const realFollowers = selectedStartup.followersList || [];
                          const totalToShow = 10;
                          const placeholders = Array(Math.max(0, totalToShow - realFollowers.length)).fill(null);
                          const allAvatars = [...realFollowers.slice(0, totalToShow), ...placeholders].slice(0, totalToShow);
                          
                          return allAvatars.map((follower, idx) => (
                            <div key={idx} style={{
                              width: "36px",
                              height: "36px",
                              borderRadius: "6px",
                              background: follower?.avatar ? "#fff" : "rgba(255,255,255,0.15)",
                              border: "2px solid rgba(255,255,255,0.3)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              overflow: "hidden"
                            }}>
                              {follower?.avatar ? (
                                <img 
                                  src={follower.avatar} 
                                  alt={follower.nome}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover"
                                  }}
                                />
                              ) : follower ? (
                                <span style={{
                                  color: "white",
                                  fontSize: "0.75em",
                                  fontWeight: "bold"
                                }}>
                                  {follower.nome?.charAt(0)?.toUpperCase()}
                                </span>
                              ) : (
                                <svg viewBox="0 0 24 24" fill="rgba(255,255,255,0.4)" width="18" height="18">
                                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                </svg>
                              )}
                            </div>
                          ));
                        })()}
                      </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Botões de ação */}
          <div style={{ 
            background: "#fff", 
            padding: isMobile ? "12px 15px" : "15px 20px",
            borderBottom: "1px solid #E8F0FE"
          }}>
            {isOwner(selectedStartup) ? (
              <div>
                {/* Botões de ação com ícones em linha horizontal */}
                <div style={{ display: "flex", gap: "8px", marginBottom: "10px", justifyContent: "center" }}>
                  <button
                    onClick={() => setShowUpdateForm(true)}
                    title="Postar Atualização"
                    style={{
                      width: "45px",
                      height: "45px",
                      borderRadius: "8px",
                      background: "#fff",
                      border: "2px solid #1B4398",
                      color: "#1B4398",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.2s ease"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#1B4398";
                      e.currentTarget.style.color = "#fff";
                      e.currentTarget.style.transform = "scale(1.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#fff";
                      e.currentTarget.style.color = "#1B4398";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                    </svg>
                  </button>
                  
                  <button
                    onClick={() => setShowJobForm(true)}
                    title="Divulgar Vaga"
                    style={{
                      width: "45px",
                      height: "45px",
                      borderRadius: "8px",
                      background: "#fff",
                      border: "2px solid #28A745",
                      color: "#28A745",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.2s ease"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#28A745";
                      e.currentTarget.style.color = "#fff";
                      e.currentTarget.style.transform = "scale(1.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#fff";
                      e.currentTarget.style.color = "#28A745";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
                      <path d="M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z"/>
                    </svg>
                  </button>
                  
                  <button
                    onClick={handleDeleteStartup}
                    title="Deletar Startup"
                    style={{
                      width: "45px",
                      height: "45px",
                      borderRadius: "8px",
                      background: "#fff",
                      border: "2px solid #DC3545",
                      color: "#DC3545",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.2s ease"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#DC3545";
                      e.currentTarget.style.color = "#fff";
                      e.currentTarget.style.transform = "scale(1.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#fff";
                      e.currentTarget.style.color = "#DC3545";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                    </svg>
                  </button>
                </div>
                
                {/* Botão seguir em baixo */}
                <button
                  className="orkut-btn"
                  style={{ 
                    width: "100%",
                    background: isFollowing(selectedStartup.id) ? "#fff" : "#D4418E",
                    color: isFollowing(selectedStartup.id) ? "#6C757D" : "#fff",
                    border: isFollowing(selectedStartup.id) ? "3px solid #6C757D" : "3px solid #D4418E",
                    fontWeight: "600",
                    padding: isMobile ? "12px 14px" : "12px 20px",
                    fontSize: isMobile ? "0.9em" : "1em",
                    transition: "all 0.2s ease",
                    borderRadius: "8px"
                  }}
                  onMouseEnter={(e) => {
                    if (isFollowing(selectedStartup.id)) {
                      e.currentTarget.style.background = "#6C757D";
                      e.currentTarget.style.color = "#fff";
                    } else {
                      e.currentTarget.style.background = "#fff";
                      e.currentTarget.style.color = "#D4418E";
                    }
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    if (isFollowing(selectedStartup.id)) {
                      e.currentTarget.style.background = "#fff";
                      e.currentTarget.style.color = "#6C757D";
                    } else {
                      e.currentTarget.style.background = "#D4418E";
                      e.currentTarget.style.color = "#fff";
                    }
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                  onClick={() => {
                    handleFollowStartup(selectedStartup.id);
                    setTimeout(() => loadStartupDetails(selectedStartup.id), 300);
                  }}
                >
                  {isFollowing(selectedStartup.id) ? "Seguindo" : "Seguir StartUp"}
                </button>
              </div>
            ) : (
              <button
                className="orkut-btn"
                style={{ 
                  width: "100%",
                  background: isFollowing(selectedStartup.id) ? "#fff" : "#D4418E",
                  color: isFollowing(selectedStartup.id) ? "#6C757D" : "#fff",
                  border: isFollowing(selectedStartup.id) ? "3px solid #6C757D" : "3px solid #D4418E",
                  fontWeight: "600",
                  padding: isMobile ? "12px 14px" : "12px 20px",
                  fontSize: isMobile ? "0.95em" : "1em",
                  transition: "all 0.2s ease",
                  borderRadius: "8px"
                }}
                onMouseEnter={(e) => {
                  if (isFollowing(selectedStartup.id)) {
                    e.currentTarget.style.background = "#6C757D";
                    e.currentTarget.style.color = "#fff";
                  } else {
                    e.currentTarget.style.background = "#fff";
                    e.currentTarget.style.color = "#D4418E";
                  }
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  if (isFollowing(selectedStartup.id)) {
                    e.currentTarget.style.background = "#fff";
                    e.currentTarget.style.color = "#6C757D";
                  } else {
                    e.currentTarget.style.background = "#D4418E";
                    e.currentTarget.style.color = "#fff";
                  }
                  e.currentTarget.style.transform = "translateY(0)";
                }}
                onClick={() => {
                  handleFollowStartup(selectedStartup.id);
                  setTimeout(() => loadStartupDetails(selectedStartup.id), 300);
                }}
              >
                {isFollowing(selectedStartup.id) ? "Seguindo" : "Seguir StartUp"}
              </button>
            )}
          </div>

          {/* Abas */}
          <div style={{ 
            background: "#fff", 
            padding: "10px", 
            borderRadius: "0",
            margin: "0",
            borderBottom: "2px solid #D9E1EE",
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            position: "sticky",
            top: 0,
            zIndex: 100,
            display: "flex",
            gap: "8px",
            overflowX: "auto",
            whiteSpace: "nowrap",
            minHeight: "35px",
            alignItems: "center"
          }}>
            <button
              className={`mobile-filter-btn ${activeTab === "updates" ? "active" : ""}`}
              onClick={() => setActiveTab("updates")}
              style={{
                background: activeTab === "updates" ? "#1B4398" : "transparent",
                color: activeTab === "updates" ? "#fff" : "#666",
                fontWeight: activeTab === "updates" ? "600" : "500",
                padding: "10px 16px",
                borderRadius: "8px",
                transition: "all 0.2s ease",
                fontSize: isMobile ? "0.85em" : "0.95em",
                flexShrink: 0
              }}
            >
              Atualizações ({selectedStartup.updates?.length || 0})
            </button>
            <button
              className={`mobile-filter-btn ${activeTab === "jobs" ? "active" : ""}`}
              onClick={() => setActiveTab("jobs")}
              style={{
                background: activeTab === "jobs" ? "#28A745" : "transparent",
                color: activeTab === "jobs" ? "#fff" : "#666",
                fontWeight: activeTab === "jobs" ? "600" : "500",
                padding: "10px 16px",
                borderRadius: "8px",
                transition: "all 0.2s ease",
                fontSize: isMobile ? "0.85em" : "0.95em",
                flexShrink: 0
              }}
            >
              Vagas ({selectedStartup.jobs?.length || 0})
            </button>
            <button
              className={`mobile-filter-btn ${activeTab === "community" ? "active" : ""}`}
              onClick={() => {
                // Fechar modal e abrir aba de comunidade
                setCommunityStartup(selectedStartup);
                setSelectedStartup(null);
                setActiveTab("community-detail");
                loadCommunityPosts(selectedStartup.id);
              }}
              style={{
                background: activeTab === "community" ? "#667eea" : "transparent",
                color: activeTab === "community" ? "#fff" : "#666",
                fontWeight: activeTab === "community" ? "600" : "500",
                padding: "10px 16px",
                borderRadius: "8px",
                transition: "all 0.2s ease",
                fontSize: isMobile ? "0.85em" : "0.95em",
                flexShrink: 0
              }}
            >
              Comunidade
            </button>
          </div>

          {/* Conteúdo das abas */}
          <div style={{ 
            padding: "20px",
            background: "#F9FAFB",
            position: "relative",
            zIndex: 1
          }}>
          {activeTab === "updates" && (
            <div>
              {selectedStartup.updates && selectedStartup.updates.length > 0 ? (
                selectedStartup.updates.map(update => (
                  <div key={update.id} className="orkut-box" style={{ 
                    marginBottom: "15px",
                    background: "#fff",
                    border: "1px solid #D9E1EE",
                    borderRadius: "12px",
                    padding: "20px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    position: "relative",
                    zIndex: 1
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.05)";
                  }}
                  >
                    {/* Header: Logo da startup + Nome da startup - Nome do autor */}
                    <div style={{ display: "flex", gap: "12px", marginBottom: "15px", alignItems: "center" }}>
                      <img 
                        src={selectedStartup.logo_url} 
                        alt={selectedStartup.name}
                        style={{
                          width: "50px",
                          height: "50px",
                          borderRadius: "8px",
                          objectFit: "contain",
                          background: "#fff",
                          padding: "4px",
                          border: "2px solid #E8F0FE",
                          flexShrink: 0
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap", wordBreak: "break-word" }}>
                          <strong style={{ color: "#1B4398", fontSize: "1.05em" }}>
                            {selectedStartup.name}
                          </strong>
                          <span style={{ color: "#999" }}>-</span>
                          <span style={{ color: "#666", fontSize: "0.95em" }}>
                            {update.author?.nome || "Usuário"}
                          </span>
                        </div>
                        <span style={{ fontSize: "0.85em", color: "#999" }}>
                          {new Date(update.created_at).toLocaleDateString("pt-BR", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric"
                          })}
                        </span>
                      </div>
                    </div>

                    {/* Título */}
                    <h4 style={{ 
                      margin: "0 0 10px 0", 
                      color: "#1B4398",
                      fontSize: "1.15em",
                      fontWeight: "600"
                    }}>
                      {update.title}
                    </h4>

                    {/* Descrição */}
                    <p style={{ 
                      margin: 0, 
                      whiteSpace: "pre-wrap",
                      color: "#333",
                      lineHeight: "1.6",
                      fontSize: "0.95em"
                    }}>
                      {update.content}
                    </p>

                    {/* Barra de Interações */}
                    <div style={{ 
                      borderTop: "1px solid #E8F0FE",
                      marginTop: "15px",
                      paddingTop: "12px",
                      display: "flex",
                      gap: "12px",
                      alignItems: "center"
                    }}>
                      <button
                        onClick={() => handleLikeUpdate(update.id)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "6px 12px",
                          borderRadius: "20px",
                          fontSize: "0.95em",
                          fontWeight: "500",
                          color: updateLikes[update.id]?.userLiked ? "#E74C3C" : "#666",
                          transition: "all 0.2s ease",
                          background: updateLikes[update.id]?.userLiked ? "#FFE8E6" : "#F8F9FA"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = updateLikes[update.id]?.userLiked ? "#FFD6D3" : "#E8F0FE";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = updateLikes[update.id]?.userLiked ? "#FFE8E6" : "#F8F9FA";
                        }}
                      >
                        {updateLikes[update.id]?.userLiked ? (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="#E74C3C">
                            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                          </svg>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                          </svg>
                        )}
                        <span>{updateLikes[update.id]?.count || 0}</span>
                      </button>

                      <button
                        onClick={() => setShowComments({...showComments, [update.id]: !showComments[update.id]})}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "6px 12px",
                          borderRadius: "20px",
                          fontSize: "0.95em",
                          fontWeight: "500",
                          color: "#666",
                          transition: "all 0.2s ease",
                          background: showComments[update.id] ? "#E8F0FE" : "#F8F9FA"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#E8F0FE";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = showComments[update.id] ? "#E8F0FE" : "#F8F9FA";
                        }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                        </svg>
                        <span>{updateComments[update.id]?.length || 0}</span>
                      </button>
                    </div>

                    {/* Seção de Comentários */}
                    {showComments[update.id] && (
                      <div style={{
                        borderTop: "1px solid #E8F0FE",
                        marginTop: "12px",
                        paddingTop: "15px"
                      }}>
                        {/* Lista de Comentários */}
                        {updateComments[update.id] && updateComments[update.id].length > 0 && (
                          <div style={{ marginBottom: "15px" }}>
                            {updateComments[update.id].map(comment => (
                              <div key={comment.id} style={{
                                display: "flex",
                                gap: "10px",
                                marginBottom: "12px",
                                padding: "10px",
                                background: "#F8F9FA",
                                borderRadius: "8px"
                              }}>
                                <img 
                                  src={comment.user?.avatar || comment.user?.foto || "https://ui-avatars.com/api/?name=" + (comment.user?.nome || "User")}
                                  alt={comment.user?.nome || "Usuário"}
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    flexShrink: 0
                                  }}
                                />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: "4px", flexWrap: "wrap" }}>
                                      <strong style={{ color: "#1B4398", fontSize: "0.9em" }}>
                                        {comment.user?.nome || "Usuário"}
                                      </strong>
                                      {comment.user?.verified && (
                                        <svg
                                          width="14"
                                          height="14"
                                          viewBox="0 0 24 24"
                                          fill="#1DA1F2"
                                          title="Perfil Verificado"
                                        >
                                          <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91s-2.52-1.27-3.91-.81c-.66-1.31-1.91-2.19-3.34-2.19s-2.67.88-3.34 2.19c-1.39-.46-2.9-.2-3.91.81s-1.27 2.52-.81 3.91c-1.31.66-2.19 1.91-2.19 3.34s.88 2.67 2.19 3.34c-.46 1.39-.2 2.9.81 3.91s2.52 1.27 3.91.81c.66 1.31 1.91 2.19 3.34 2.19s2.67-.88 3.34-2.19c1.39.46 2.9.2 3.91-.81s1.27-2.52.81-3.91c1.31-.66 2.19-1.91 2.19-3.34zm-11.71 4.2L6.8 12.46l1.41-1.42L10.54 13.38l5.65-5.65 1.42 1.42-7.12 7.05z"/>
                                        </svg>
                                      )}
                                      <span style={{ 
                                        fontSize: "0.8em", 
                                        color: "#999",
                                        marginLeft: "4px"
                                      }}>
                                        {new Date(comment.created_at).toLocaleDateString("pt-BR", {
                                          day: "2-digit",
                                          month: "short"
                                        })}
                                      </span>
                                    </div>
                                    {comment.user_id === user?.id && (
                                      <button
                                        onClick={() => handleDeleteComment(update.id, comment.id)}
                                        style={{
                                          background: "none",
                                          border: "none",
                                          cursor: "pointer",
                                          color: "#999",
                                          padding: "2px 6px",
                                          fontSize: "0.85em",
                                          transition: "color 0.2s ease",
                                          display: "flex",
                                          alignItems: "center"
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.color = "#E74C3C"}
                                        onMouseLeave={(e) => e.currentTarget.style.color = "#999"}
                                        title="Deletar comentário"
                                      >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <polyline points="3,6 5,6 21,6"/>
                                          <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6M8,6V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"/>
                                          <line x1="10" y1="11" x2="10" y2="17"/>
                                          <line x1="14" y1="11" x2="14" y2="17"/>
                                        </svg>
                                      </button>
                                    )}
                                  </div>
                                  <p style={{
                                    margin: "6px 0 0 0",
                                    color: "#333",
                                    fontSize: "0.9em",
                                    lineHeight: "1.4",
                                    wordWrap: "break-word"
                                  }}>
                                    {comment.content}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Input de Novo Comentário */}
                        <div style={{ display: "flex", gap: "10px", alignItems: "start" }}>
                          <img 
                            src={user?.foto || "/default-avatar.png"}
                            alt="Você"
                            style={{
                              width: "32px",
                              height: "32px",
                              borderRadius: "50%",
                              objectFit: "cover",
                              flexShrink: 0,
                              marginTop: "4px"
                            }}
                          />
                          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
                            <textarea
                              value={commentText[update.id] || ""}
                              onChange={(e) => setCommentText({...commentText, [update.id]: e.target.value})}
                              placeholder="Escreva um comentário..."
                              style={{
                                width: "100%",
                                padding: "10px",
                                border: "1px solid #D9E1EE",
                                borderRadius: "8px",
                                fontSize: "0.9em",
                                resize: "vertical",
                                minHeight: "60px",
                                fontFamily: "inherit"
                              }}
                            />
                            <button
                              onClick={() => handleAddComment(update.id)}
                              disabled={!commentText[update.id]?.trim()}
                              style={{
                                alignSelf: "flex-end",
                                padding: "8px 20px",
                                background: commentText[update.id]?.trim() ? "#1B4398" : "#ccc",
                                color: "#fff",
                                border: "none",
                                borderRadius: "20px",
                                cursor: commentText[update.id]?.trim() ? "pointer" : "not-allowed",
                                fontSize: "0.9em",
                                fontWeight: "500",
                                transition: "all 0.2s ease"
                              }}
                              onMouseEnter={(e) => {
                                if (commentText[update.id]?.trim()) {
                                  e.currentTarget.style.background = "#153470";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (commentText[update.id]?.trim()) {
                                  e.currentTarget.style.background = "#1B4398";
                                }
                              }}
                            >
                              Comentar
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div style={{ 
                  textAlign: "center", 
                  padding: "60px 20px",
                  background: "#fff",
                  borderRadius: "12px",
                  border: "2px dashed #D9E1EE"
                }}>
                  <p style={{ color: "#999", margin: 0, fontSize: "1.1em", fontWeight: "500" }}>
                    Nenhuma atualização ainda
                  </p>
                  <p style={{ color: "#BBB", margin: "8px 0 0 0", fontSize: "0.9em" }}>
                    As novidades da startup aparecerão aqui
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === "jobs" && (
            <div>
              {selectedStartup.jobs && selectedStartup.jobs.length > 0 ? (
                selectedStartup.jobs.map(job => (
                  <div key={job.id} className="orkut-box" style={{ 
                    marginBottom: "15px",
                    background: "#fff",
                    border: "1px solid #D9E1EE",
                    borderRadius: "12px",
                    padding: "20px",
                    borderLeft: "4px solid #28A745",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    position: "relative",
                    zIndex: 1
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.05)";
                  }}
                  >
                    <h4 style={{ 
                      margin: "0 0 12px 0", 
                      color: "#1B4398",
                      fontSize: "1.1em",
                      fontWeight: "600"
                    }}>
                      {job.title}
                    </h4>
                    <div style={{ marginBottom: "12px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      <span style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: "4px 12px",
                        background: "#28A745",
                        color: "#fff",
                        borderRadius: "20px",
                        fontSize: "0.75em",
                        fontWeight: "500"
                      }}>
                        <svg viewBox="0 0 24 24" fill="white" width="14" height="14">
                          <path d="M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z"/>
                        </svg>
                        {job.type === "full-time" ? "Tempo Integral" : 
                         job.type === "part-time" ? "Meio Período" :
                         job.type === "contract" ? "Contrato" :
                         job.type === "internship" ? "Estágio" :
                         job.type === "volunteer" ? "Voluntário" : job.type}
                      </span>
                      {job.location && (
                        <span style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          padding: "4px 12px",
                          background: "#F0F2F5",
                          color: "#666",
                          borderRadius: "20px",
                          fontSize: "0.75em",
                          fontWeight: "500"
                        }}>
                          <svg viewBox="0 0 24 24" fill="#666" width="14" height="14">
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                          </svg>
                          {job.location}
                        </span>
                      )}
                      {job.salary_range && (
                        <span style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          padding: "4px 12px",
                          background: "#FFF3CD",
                          color: "#856404",
                          borderRadius: "20px",
                          fontSize: "0.75em",
                          fontWeight: "500"
                        }}>
                          <svg viewBox="0 0 24 24" fill="#856404" width="14" height="14">
                            <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
                          </svg>
                          {job.salary_range}
                        </span>
                      )}
                    </div>
                    <p style={{ 
                      margin: "0 0 12px 0", 
                      whiteSpace: "pre-wrap",
                      color: "#333",
                      lineHeight: "1.5"
                    }}>
                      {job.description}
                    </p>
                    {job.requirements && (
                      <div style={{ 
                        marginTop: "12px", 
                        padding: "12px", 
                        background: "#F8F9FA", 
                        borderRadius: "8px",
                        borderLeft: "3px solid #1B4398"
                      }}>
                        <strong style={{ 
                          fontSize: "0.9em", 
                          color: "#1B4398",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px"
                        }}>
                          <svg viewBox="0 0 24 24" fill="#1B4398" width="16" height="16">
                            <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
                          </svg>
                          Requisitos:
                        </strong>
                        <p style={{ 
                          margin: "8px 0 0 0", 
                          fontSize: "0.9em", 
                          whiteSpace: "pre-wrap",
                          color: "#666",
                          lineHeight: "1.5"
                        }}>
                          {job.requirements}
                        </p>
                      </div>
                    )}
                    <div style={{ 
                      marginTop: "12px", 
                      paddingTop: "12px",
                      borderTop: "1px solid #E8F0FE",
                      fontSize: "0.85em", 
                      color: "#999",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}>
                      <span>
                        Publicado em {new Date(job.created_at).toLocaleDateString("pt-BR", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric"
                        })}
                      </span>
                      <button 
                        className="orkut-btn"
                        style={{ fontSize: "0.9em", padding: "6px 16px" }}
                        onClick={() => showToast("Sistema de candidatura em breve!", "info")}
                      >
                        candidatar-se
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ 
                  textAlign: "center", 
                  padding: "60px 20px",
                  background: "#fff",
                  borderRadius: "12px",
                  border: "2px dashed #D9E1EE"
                }}>
                  <p style={{ color: "#999", margin: 0, fontSize: "1.1em", fontWeight: "500" }}>
                    Nenhuma vaga disponível no momento
                  </p>
                  <p style={{ color: "#BBB", margin: "8px 0 0 0", fontSize: "0.9em" }}>
                    Fique atento para futuras oportunidades
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ABA COMUNIDADE */}
          {activeTab === "community" && (
            <div>
              {/* Campo para postar */}
              <div style={{
                background: "#fff",
                borderRadius: "12px",
                padding: isMobile ? "15px" : "20px",
                marginBottom: "20px",
                border: "1px solid #D9E1EE",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
              }}>
                <h4 style={{ 
                  margin: "0 0 12px 0",
                  color: "#1B4398",
                  fontSize: isMobile ? "1em" : "1.1em",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px"
                }}>
                  <svg 
                    viewBox="0 0 24 24" 
                    fill="#667eea" 
                    width={isMobile ? "20" : "24"} 
                    height={isMobile ? "20" : "24"}
                    style={{
                      minWidth: isMobile ? "20px" : "24px",
                      minHeight: isMobile ? "20px" : "24px",
                      maxWidth: isMobile ? "20px" : "24px",
                      maxHeight: isMobile ? "20px" : "24px",
                      flexShrink: 0,
                      display: "block"
                    }}
                  >
                    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
                  </svg>
                  Poste na Comunidade
                </h4>
                <textarea
                  value={communityPostText}
                  onChange={(e) => setCommunityPostText(e.target.value)}
                  placeholder={isMobile ? "Compartilhe com a comunidade..." : "Compartilhe novidades, dúvidas ou discussões com a comunidade da startup..."}
                  style={{
                    width: "100%",
                    minHeight: isMobile ? "80px" : "100px",
                    padding: isMobile ? "10px" : "12px",
                    border: "2px solid #E8F0FE",
                    borderRadius: "8px",
                    fontSize: isMobile ? "0.9em" : "0.95em",
                    fontFamily: "inherit",
                    resize: "vertical",
                    marginBottom: "12px",
                    boxSizing: "border-box"
                  }}
                />
                <button
                  onClick={handlePostToCommunity}
                  disabled={!communityPostText.trim()}
                  style={{
                    background: communityPostText.trim() ? "#667eea" : "#ccc",
                    color: "#fff",
                    border: "none",
                    padding: isMobile ? "10px 20px" : "10px 24px",
                    borderRadius: "8px",
                    fontSize: "0.95em",
                    fontWeight: "600",
                    cursor: communityPostText.trim() ? "pointer" : "not-allowed",
                    transition: "all 0.2s ease"
                  }}
                  onMouseEnter={(e) => {
                    if (communityPostText.trim()) {
                      e.currentTarget.style.background = "#5568d3";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (communityPostText.trim()) {
                      e.currentTarget.style.background = "#667eea";
                    }
                  }}
                >
                  Publicar
                </button>
              </div>

              {/* Lista de posts */}
              {loadingCommunity ? (
                <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                  Carregando posts...
                </div>
              ) : communityPosts.length > 0 ? (
                communityPosts.map(post => (
                  <div key={post.id} style={{
                    background: "#fff",
                    borderRadius: "12px",
                    padding: isMobile ? "15px" : "20px",
                    marginBottom: "15px",
                    border: "1px solid #D9E1EE",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    transition: "all 0.2s ease"
                  }}
                  onMouseEnter={(e) => {
                    if (!isMobile) e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.05)";
                  }}
                  onTouchStart={() => {
                    if (isMobile && post.author_id === user.id) {
                      const timer = setTimeout(() => {
                        setConfirmModal({
                          message: "Deseja excluir este post?",
                          onConfirm: () => handleDeleteCommunityPost(post.id)
                        });
                      }, 500);
                      setLongPressTimer(timer);
                    }
                  }}
                  onTouchEnd={() => {
                    if (longPressTimer) {
                      clearTimeout(longPressTimer);
                      setLongPressTimer(null);
                    }
                  }}
                  onTouchMove={() => {
                    if (longPressTimer) {
                      clearTimeout(longPressTimer);
                      setLongPressTimer(null);
                    }
                  }}
                  >
                    {/* Header do post */}
                    <div style={{ display: "flex", gap: isMobile ? "10px" : "12px", marginBottom: "12px", alignItems: "flex-start" }}>
                      <img
                        src={post.author?.avatar || "https://i.pinimg.com/736x/d4/8f/8f/d48f8f59f2de25750b13084b59301201.jpg"}
                        alt={post.author?.nome}
                        style={{
                          width: isMobile ? "38px" : "45px",
                          height: isMobile ? "38px" : "45px",
                          minWidth: isMobile ? "38px" : "45px",
                          minHeight: isMobile ? "38px" : "45px",
                          maxWidth: isMobile ? "38px" : "45px",
                          maxHeight: isMobile ? "38px" : "45px",
                          borderRadius: "50%",
                          objectFit: "cover",
                          border: "2px solid #E8F0FE",
                          flexShrink: 0,
                          display: "block"
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                          <strong style={{ 
                            color: "#1B4398", 
                            fontSize: isMobile ? "0.9em" : "1em"
                          }}>
                            {post.author?.nome || "Usuário"}
                          </strong>
                          {post.author?.verified && (
                            <svg 
                              viewBox="0 0 24 24" 
                              fill="#1DA1F2" 
                              width={isMobile ? "14" : "16"} 
                              height={isMobile ? "14" : "16"}
                              style={{ 
                                minWidth: isMobile ? "14px" : "16px",
                                minHeight: isMobile ? "14px" : "16px",
                                maxWidth: isMobile ? "14px" : "16px",
                                maxHeight: isMobile ? "14px" : "16px",
                                flexShrink: 0,
                                display: "inline-block",
                                verticalAlign: "middle"
                              }}
                            >
                              <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z"/>
                            </svg>
                          )}
                        </div>
                        <div style={{ fontSize: isMobile ? "0.7em" : "0.75em", color: "#999", marginTop: "2px" }}>
                          {getTimeAgo(post.created_at)}
                        </div>
                      </div>
                      {!isMobile && post.author_id === user.id && (
                        <div style={{ position: "relative", marginLeft: "auto" }}>
                          <button
                            onClick={() => setOpenMenus(prev => ({
                              ...prev,
                              [`post_${post.id}`]: !prev[`post_${post.id}`]
                            }))}
                            style={{
                              background: "transparent",
                              border: "none",
                              color: "#999",
                              cursor: "pointer",
                              padding: "0",
                              width: "36px",
                              height: "36px",
                              minWidth: "36px",
                              minHeight: "36px",
                              maxWidth: "36px",
                              maxHeight: "36px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.2s",
                              fontSize: "20px",
                              fontWeight: "bold",
                              flexShrink: 0
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.color = "#666"}
                            onMouseLeave={(e) => e.currentTarget.style.color = "#999"}
                            title="Mais opções"
                          >
                            ⋮
                          </button>
                          {openMenus[`post_${post.id}`] && (
                            <>
                              <div 
                                style={{
                                  position: "fixed",
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  zIndex: 998
                                }}
                                onClick={() => setOpenMenus(prev => ({ ...prev, [`post_${post.id}`]: false }))}
                              />
                              <div style={{
                                position: "absolute",
                                top: "100%",
                                right: 0,
                                marginTop: "4px",
                                background: "white",
                                border: "1px solid #ddd",
                                borderRadius: "8px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                zIndex: 999,
                                minWidth: "150px"
                              }}>
                                <button
                                  onClick={() => {
                                    setOpenMenus(prev => ({ ...prev, [`post_${post.id}`]: false }));
                                    setConfirmModal({
                                      message: "Tem certeza que deseja deletar este post?",
                                      onConfirm: () => handleDeleteCommunityPost(post.id)
                                    });
                                  }}
                                  style={{
                                    width: "100%",
                                    padding: "12px 16px",
                                    background: "transparent",
                                    border: "none",
                                    textAlign: "left",
                                    cursor: "pointer",
                                    color: "#dc3545",
                                    fontSize: "0.9em",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    transition: "background 0.2s"
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = "#f8f9fa"}
                                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                                >
                                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                                  </svg>
                                  Excluir post
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Conteúdo */}
                    <p style={{
                      margin: "0 0 12px 0",
                      color: "#333",
                      whiteSpace: "pre-wrap",
                      lineHeight: "1.6",
                      fontSize: isMobile ? "0.9em" : "0.95em",
                      wordBreak: "break-word"
                    }}>
                      {post.content}
                    </p>

                    {/* Reações */}
                    <div style={{
                      display: "flex",
                      gap: isMobile ? "8px" : "10px",
                      alignItems: "center",
                      marginBottom: "12px",
                      paddingTop: "10px",
                      borderTop: "1px solid #f0f0f0"
                    }}>
                      <div style={{ position: "relative" }}>
                        <button
                          onClick={() => {
                            if (!communityReactions[post.id]) {
                              loadCommunityReactions(post.id);
                            }
                            setShowReactionPicker(showReactionPicker === post.id ? null : post.id);
                          }}
                          style={{
                            background: "transparent",
                            border: "1px solid #ddd",
                            borderRadius: "20px",
                            padding: isMobile ? "8px 14px" : "6px 12px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            fontSize: isMobile ? "0.85em" : "0.9em",
                            transition: "all 0.2s",
                            minWidth: isMobile ? "44px" : "auto",
                            minHeight: isMobile ? "44px" : "auto",
                            justifyContent: "center"
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = "#f5f5f5"}
                          onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                        >
                          {(() => {
                            const reactions = communityReactions[post.id] || [];
                            const userReaction = reactions.find(r => r.user_id === user.id);
                            const reactionEmojis = {
                              like: "👍",
                              love: "❤️",
                              celebrate: "🎉",
                              support: "💪",
                              insightful: "💡"
                            };
                            return userReaction ? reactionEmojis[userReaction.reaction_type] : "👍";
                          })()}
                          <span>{(communityReactions[post.id] || []).length || ""}</span>
                        </button>

                        {showReactionPicker === post.id && (
                          <div style={{
                            position: "absolute",
                            bottom: "100%",
                            left: "0",
                            marginBottom: "5px",
                            background: "#fff",
                            border: "1px solid #ddd",
                            borderRadius: "25px",
                            padding: isMobile ? "10px 14px" : "8px 12px",
                            display: "flex",
                            gap: isMobile ? "10px" : "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                            zIndex: 100
                          }}>
                            {[
                              { type: "like", emoji: "👍" },
                              { type: "love", emoji: "❤️" },
                              { type: "celebrate", emoji: "🎉" },
                              { type: "support", emoji: "💪" },
                              { type: "insightful", emoji: "💡" }
                            ].map(reaction => (
                              <button
                                key={reaction.type}
                                onClick={() => handleCommunityReaction(post.id, reaction.type)}
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  fontSize: isMobile ? "1.8em" : "1.5em",
                                  cursor: "pointer",
                                  transition: "transform 0.2s",
                                  minWidth: isMobile ? "44px" : "auto",
                                  minHeight: isMobile ? "44px" : "auto",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center"
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.3)"}
                                onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                                title={reaction.type}
                              >
                                {reaction.emoji}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => {
                          if (!communityComments[post.id]) {
                            loadCommunityComments(post.id);
                          }
                          // Toggle expandir/colapsar comentários (começam fechados)
                          setCollapsedComments(prev => ({
                            ...prev,
                            [post.id]: prev[post.id] === undefined ? false : !prev[post.id]
                          }));
                        }}
                        style={{
                          background: "transparent",
                          border: "1px solid #ddd",
                          borderRadius: "20px",
                          padding: isMobile ? "8px 14px" : "6px 12px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          fontSize: isMobile ? "0.85em" : "0.9em",
                          transition: "all 0.2s",
                          minWidth: isMobile ? "44px" : "auto",
                          minHeight: isMobile ? "44px" : "auto",
                          justifyContent: "center"
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "#f5f5f5"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                      >
                        <svg 
                          viewBox="0 0 24 24" 
                          fill="currentColor" 
                          width="16" 
                          height="16"
                          style={{
                            minWidth: "16px",
                            minHeight: "16px",
                            maxWidth: "16px",
                            maxHeight: "16px",
                            flexShrink: 0,
                            display: "block"
                          }}
                        >
                          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
                        </svg>
                        <span>{(communityComments[post.id] || []).length || 0}</span>
                      </button>
                    </div>

                    {/* Comentários */}
                    {communityComments[post.id] && collapsedComments[post.id] === false && (
                      <div style={{
                        marginTop: "12px",
                        paddingTop: "12px",
                        borderTop: "1px solid #f0f0f0"
                      }}>
                        {/* Lista de comentários */}
                        {communityComments[post.id].filter(c => !c.parent_comment_id).map(comment => {
                          const replies = communityComments[post.id].filter(r => r.parent_comment_id === comment.id);
                          const replyKey = `${post.id}_${comment.id}`;
                          
                          return (
                            <div key={comment.id} style={{ marginBottom: isMobile ? "8px" : "10px" }}>
                              {/* Comentário principal */}
                              <div style={{
                                padding: isMobile ? "5px 6px" : "6px 8px",
                                background: "#f8f9fa",
                                borderRadius: "6px"
                              }}
                              onTouchStart={() => {
                                if (isMobile && comment.author_id === user.id) {
                                  const timer = setTimeout(() => {
                                    setConfirmModal({
                                      message: "Deseja excluir este comentário?",
                                      onConfirm: () => handleDeleteCommunityComment(post.id, comment.id)
                                    });
                                  }, 500);
                                  setLongPressTimer(timer);
                                }
                              }}
                              onTouchEnd={() => {
                                if (longPressTimer) {
                                  clearTimeout(longPressTimer);
                                  setLongPressTimer(null);
                                }
                              }}
                              onTouchMove={() => {
                                if (longPressTimer) {
                                  clearTimeout(longPressTimer);
                                  setLongPressTimer(null);
                                }
                              }}
                              >
                                <div style={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: isMobile ? "5px" : "6px",
                                  marginBottom: "3px"
                                }}>
                                  <img
                                    src={comment.author?.avatar || "https://via.placeholder.com/32"}
                                    alt={comment.author?.nome}
                                    style={{
                                      width: isMobile ? "18px" : "22px",
                                      height: isMobile ? "18px" : "22px",
                                      minWidth: isMobile ? "18px" : "22px",
                                      minHeight: isMobile ? "18px" : "22px",
                                      maxWidth: isMobile ? "18px" : "22px",
                                      maxHeight: isMobile ? "18px" : "22px",
                                      borderRadius: "50%",
                                      objectFit: "cover",
                                      flexShrink: 0,
                                      display: "block"
                                    }}
                                  />
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: "inline-flex", alignItems: "center", gap: "3px" }}>
                                  <strong style={{ 
                                    fontSize: isMobile ? "0.8em" : "0.85em", 
                                    color: "#333"
                                  }}>
                                    {comment.author?.nome}
                                  </strong>
                                  {comment.author?.verified && (
                                    <svg 
                                      viewBox="0 0 24 24" 
                                      fill="#1DA1F2" 
                                      width={isMobile ? "12" : "14"}
                                      height={isMobile ? "12" : "14"}
                                      style={{ 
                                        minWidth: isMobile ? "12px" : "14px",
                                        minHeight: isMobile ? "12px" : "14px",
                                        maxWidth: isMobile ? "12px" : "14px",
                                        maxHeight: isMobile ? "12px" : "14px",
                                        flexShrink: 0,
                                        display: "inline-block",
                                        verticalAlign: "middle"
                                      }}
                                    >
                                      <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z"/>
                                    </svg>
                                  )}
                                  </div>
                                  <div style={{ fontSize: isMobile ? "0.65em" : "0.7em", color: "#999", marginTop: "2px" }}>
                                    {getTimeAgo(comment.created_at)}
                                  </div>
                                  </div>
                                  {!isMobile && comment.author_id === user.id && (
                                    <div style={{ position: "relative", marginLeft: "auto" }}>
                                      <button
                                        onClick={() => setOpenMenus(prev => ({
                                          ...prev,
                                          [`comment_${comment.id}`]: !prev[`comment_${comment.id}`]
                                        }))}
                                        style={{
                                          background: "transparent",
                                          border: "none",
                                          color: "#999",
                                          cursor: "pointer",
                                          padding: "0",
                                          width: "32px",
                                          height: "32px",
                                          minWidth: "32px",
                                          minHeight: "32px",
                                          maxWidth: "32px",
                                          maxHeight: "32px",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          transition: "all 0.2s",
                                          fontSize: "16px",
                                          fontWeight: "bold",
                                          flexShrink: 0
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.color = "#666"}
                                        onMouseLeave={(e) => e.currentTarget.style.color = "#999"}
                                        title="Mais opções"
                                      >
                                        ⋮
                                      </button>
                                      {openMenus[`comment_${comment.id}`] && (
                                        <>
                                          <div 
                                            style={{
                                              position: "fixed",
                                              top: 0,
                                              left: 0,
                                              right: 0,
                                              bottom: 0,
                                              zIndex: 998
                                            }}
                                            onClick={() => setOpenMenus(prev => ({ ...prev, [`comment_${comment.id}`]: false }))}
                                          />
                                          <div style={{
                                            position: "absolute",
                                            top: "100%",
                                            right: 0,
                                            marginTop: "4px",
                                            background: "white",
                                            border: "1px solid #ddd",
                                            borderRadius: "8px",
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                            zIndex: 999,
                                            minWidth: "150px"
                                          }}>
                                            <button
                                              onClick={() => {
                                                setOpenMenus(prev => ({ ...prev, [`comment_${comment.id}`]: false }));
                                                setConfirmModal({
                                                  message: "Tem certeza que deseja deletar este comentário?",
                                                  onConfirm: () => handleDeleteCommunityComment(post.id, comment.id)
                                                });
                                              }}
                                              style={{
                                                width: "100%",
                                                padding: "12px 16px",
                                                background: "transparent",
                                                border: "none",
                                                textAlign: "left",
                                                cursor: "pointer",
                                                color: "#dc3545",
                                                fontSize: "0.85em",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                transition: "background 0.2s",
                                                borderRadius: "8px"
                                              }}
                                              onMouseEnter={(e) => e.currentTarget.style.background = "#f8f9fa"}
                                              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                                            >
                                              <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                                                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                                              </svg>
                                              Excluir comentário
                                            </button>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <p style={{
                                  margin: "0 0 4px 0",
                                  fontSize: isMobile ? "0.7em" : "0.8em",
                                  color: "#555",
                                  lineHeight: "1.25",
                                  wordBreak: "break-word",
                                  padding: isMobile ? "0" : "0 2px"
                                }}>
                                  {comment.content}
                                </p>
                                
                                {/* Botão Responder */}
                                <button
                                  onClick={() => setReplyingTo(prev => ({
                                    ...prev,
                                    [replyKey]: !prev[replyKey]
                                  }))}
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    color: "#667eea",
                                    cursor: "pointer",
                                    fontSize: isMobile ? "0.65em" : "0.7em",
                                    padding: "1px 0",
                                    fontWeight: "600",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "3px"
                                  }}
                                >
                                  <svg viewBox="0 0 24 24" fill="currentColor" width={isMobile ? "12" : "13"} height={isMobile ? "12" : "13"}>
                                    <path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z"/>
                                  </svg>
                                  Responder {replies.length > 0 && `(${replies.length})`}
                                </button>
                              </div>

                              {/* Respostas */}
                              {replies.length > 0 && (
                                <div style={{
                                  marginLeft: isMobile ? "8px" : "16px",
                                  marginTop: "4px",
                                  borderLeft: "2px solid #e0e0e0",
                                  paddingLeft: isMobile ? "5px" : "8px"
                                }}>
                                  {replies.map(reply => (
                                    <div key={reply.id} style={{
                                      marginBottom: "4px",
                                      padding: isMobile ? "4px 5px" : "5px 6px",
                                      background: "#f0f2f5",
                                      borderRadius: "5px"
                                    }}
                                    onTouchStart={() => {
                                      if (isMobile && reply.author_id === user.id) {
                                        const timer = setTimeout(() => {
                                          setConfirmModal({
                                            message: "Deseja excluir esta resposta?",
                                            onConfirm: () => handleDeleteCommunityComment(post.id, reply.id)
                                          });
                                        }, 500);
                                        setLongPressTimer(timer);
                                      }
                                    }}
                                    onTouchEnd={() => {
                                      if (longPressTimer) {
                                        clearTimeout(longPressTimer);
                                        setLongPressTimer(null);
                                      }
                                    }}
                                    onTouchMove={() => {
                                      if (longPressTimer) {
                                        clearTimeout(longPressTimer);
                                        setLongPressTimer(null);
                                      }
                                    }}
                                    >
                                      <div style={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: isMobile ? "4px" : "5px",
                                        marginBottom: "2px"
                                      }}>
                                        <img
                                          src={reply.author?.avatar || "https://via.placeholder.com/32"}
                                          alt={reply.author?.nome}
                                          style={{
                                            width: isMobile ? "14px" : "16px",
                                            height: isMobile ? "14px" : "16px",
                                            minWidth: isMobile ? "14px" : "16px",
                                            minHeight: isMobile ? "14px" : "16px",
                                            maxWidth: isMobile ? "14px" : "16px",
                                            maxHeight: isMobile ? "14px" : "16px",
                                            borderRadius: "50%",
                                            objectFit: "cover",
                                            flexShrink: 0,
                                            display: "block"
                                          }}
                                        />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: "inline-flex", alignItems: "center", gap: "2px" }}>
                                        <strong style={{ 
                                          fontSize: isMobile ? "0.68em" : "0.75em", 
                                          color: "#333"
                                        }}>
                                          {reply.author?.nome}
                                        </strong>
                                        {reply.author?.verified && (
                                          <svg 
                                            viewBox="0 0 24 24" 
                                            fill="#1DA1F2" 
                                            width="12" 
                                            height="12" 
                                            style={{ 
                                              minWidth: "12px",
                                              minHeight: "12px",
                                              maxWidth: "12px",
                                              maxHeight: "12px",
                                              flexShrink: 0,
                                              display: "inline-block",
                                              verticalAlign: "middle"
                                            }}
                                          >
                                            <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z"/>
                                          </svg>
                                        )}
                                        </div>
                                        <div style={{ fontSize: isMobile ? "0.58em" : "0.62em", color: "#999", marginTop: "1px" }}>
                                          {getTimeAgo(reply.created_at)}
                                        </div>
                                        </div>
                                        {!isMobile && reply.author_id === user.id && (
                                          <div style={{ position: "relative", marginLeft: "auto" }}>
                                            <button
                                              onClick={() => setOpenMenus(prev => ({
                                                ...prev,
                                                [`reply_${reply.id}`]: !prev[`reply_${reply.id}`]
                                              }))}
                                              style={{
                                                background: "transparent",
                                                border: "none",
                                                color: "#999",
                                                cursor: "pointer",
                                                padding: "0",
                                                width: "28px",
                                                height: "28px",
                                                minWidth: "28px",
                                                minHeight: "28px",
                                                maxWidth: "28px",
                                                maxHeight: "28px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                transition: "all 0.2s",
                                                fontSize: "14px",
                                                fontWeight: "bold",
                                                flexShrink: 0
                                              }}
                                              onMouseEnter={(e) => e.currentTarget.style.color = "#666"}
                                              onMouseLeave={(e) => e.currentTarget.style.color = "#999"}
                                              title="Mais opções"
                                            >
                                              ⋮
                                            </button>
                                            {openMenus[`reply_${reply.id}`] && (
                                              <>
                                                <div 
                                                  style={{
                                                    position: "fixed",
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    zIndex: 998
                                                  }}
                                                  onClick={() => setOpenMenus(prev => ({ ...prev, [`reply_${reply.id}`]: false }))}
                                                />
                                                <div style={{
                                                  position: "absolute",
                                                  top: "100%",
                                                  right: 0,
                                                  marginTop: "4px",
                                                  background: "white",
                                                  border: "1px solid #ddd",
                                                  borderRadius: "8px",
                                                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                                  zIndex: 999,
                                                  minWidth: "140px"
                                                }}>
                                                  <button
                                                    onClick={() => {
                                                      setOpenMenus(prev => ({ ...prev, [`reply_${reply.id}`]: false }));
                                                      setConfirmModal({
                                                        message: "Tem certeza que deseja deletar esta resposta?",
                                                        onConfirm: () => handleDeleteCommunityComment(post.id, reply.id)
                                                      });
                                                    }}
                                                    style={{
                                                      width: "100%",
                                                      padding: "10px 14px",
                                                      background: "transparent",
                                                      border: "none",
                                                      textAlign: "left",
                                                      cursor: "pointer",
                                                      color: "#dc3545",
                                                      fontSize: "0.8em",
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: "6px",
                                                      transition: "background 0.2s",
                                                      borderRadius: "8px"
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.background = "#f8f9fa"}
                                                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                                                  >
                                                    <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
                                                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                                                    </svg>
                                                    Excluir resposta
                                                  </button>
                                                </div>
                                              </>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                      <p style={{
                                        margin: "0",
                                        fontSize: isMobile ? "0.65em" : "0.7em",
                                        color: "#555",
                                        lineHeight: "1.25",
                                        wordBreak: "break-word"
                                      }}>
                                        {reply.content}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Campo para responder */}
                              {replyingTo[replyKey] && (
                                <div style={{
                                  marginLeft: isMobile ? "12px" : "20px",
                                  marginTop: "6px",
                                  display: "flex",
                                  gap: "5px"
                                }}>
                                  <input
                                    type="text"
                                    value={communityCommentText[`${post.id}_reply_${comment.id}`] || ""}
                                    onChange={(e) => setCommunityCommentText(prev => ({
                                      ...prev,
                                      [`${post.id}_reply_${comment.id}`]: e.target.value
                                    }))}
                                    onKeyPress={(e) => {
                                      if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        handleCommunityComment(post.id, comment.id);
                                      }
                                    }}
                                    placeholder="Escreva uma resposta..."
                                    style={{
                                      flex: 1,
                                      padding: isMobile ? "6px 8px" : "5px 8px",
                                      border: "1px solid #ddd",
                                      borderRadius: "12px",
                                      fontSize: isMobile ? "0.7em" : "0.75em",
                                      outline: "none"
                                    }}
                                  />
                                  <button
                                    onClick={() => handleCommunityComment(post.id, comment.id)}
                                    disabled={!communityCommentText[`${post.id}_reply_${comment.id}`]?.trim()}
                                    style={{
                                      background: communityCommentText[`${post.id}_reply_${comment.id}`]?.trim() ? "#667eea" : "#ccc",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: "12px",
                                      padding: isMobile ? "6px 12px" : "5px 10px",
                                      cursor: communityCommentText[`${post.id}_reply_${comment.id}`]?.trim() ? "pointer" : "not-allowed",
                                      fontSize: isMobile ? "0.7em" : "0.75em",
                                      fontWeight: "600"
                                    }}
                                  >
                                    Enviar
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* Campo para novo comentário */}
                        <div style={{
                          display: "flex",
                          gap: isMobile ? "5px" : "6px",
                          marginTop: "10px"
                        }}>
                          <input
                            type="text"
                            value={communityCommentText[post.id] || ""}
                            onChange={(e) => setCommunityCommentText(prev => ({
                              ...prev,
                              [post.id]: e.target.value
                            }))}
                            onKeyPress={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                handleCommunityComment(post.id);
                              }
                            }}
                            placeholder={isMobile ? "Comentar..." : "Escreva um comentário..."}
                            style={{
                              flex: 1,
                              padding: isMobile ? "7px 10px" : "6px 10px",
                              border: "1px solid #ddd",
                              borderRadius: "16px",
                              fontSize: isMobile ? "0.75em" : "0.8em",
                              outline: "none"
                            }}
                          />
                          <button
                            onClick={() => handleCommunityComment(post.id)}
                            disabled={!communityCommentText[post.id]?.trim()}
                            style={{
                              background: communityCommentText[post.id]?.trim() ? "#667eea" : "#ccc",
                              color: "#fff",
                              border: "none",
                              borderRadius: "16px",
                              padding: isMobile ? "7px 14px" : "6px 12px",
                              cursor: communityCommentText[post.id]?.trim() ? "pointer" : "not-allowed",
                              fontSize: isMobile ? "0.75em" : "0.8em",
                              fontWeight: "600",
                              minWidth: isMobile ? "70px" : "auto"
                            }}
                          >
                            Enviar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  background: "#fff",
                  borderRadius: "12px",
                  border: "2px dashed #D9E1EE"
                }}>
                  <svg viewBox="0 0 24 24" fill="#ccc" width="64" height="64" style={{ marginBottom: "16px" }}>
                    <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM9 11H7V9h2v2zm4 0h-2V9h2v2zm4 0h-2V9h2v2z"/>
                  </svg>
                  <p style={{ color: "#999", margin: 0, fontSize: "1.1em", fontWeight: "500" }}>
                    Nenhuma mensagem na comunidade ainda
                  </p>
                  <p style={{ color: "#BBB", margin: "8px 0 0 0", fontSize: "0.9em" }}>
                    Seja o primeiro a iniciar uma conversa!
                  </p>
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      </div>
  );
  };

  if (loading) {
    return (
      <div className="orkut-loading-screen">
        <div className="orkut-loading-content">
          <h1 className="orkut-loading-title">toNetwork</h1>
          <div className="orkut-loading-text">Carregando StartUps...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="orkut-main-layout">
      {/* Header Desktop */}
      <div className="orkut-topbar startup-topbar-desktop">
        <div className="orkut-topbar-left">
          <a href="#" onClick={(e) => { e.preventDefault(); onBack(); }}>Home</a>
          <a href="#" onClick={(e) => { e.preventDefault(); onNav("profile"); }}>Perfil</a>
          <a href="#" className="active">StartUps</a>
          <a href="#" onClick={(e) => { e.preventDefault(); onNav("revistas"); }}>Revista</a>
          <a href="#" onClick={(e) => { e.preventDefault(); onNav("friends"); }}>Amigos</a>
        </div>
        <div className="orkut-topbar-network">
          <div className="orkut-topbar-left-content">
            <span className="orkut-network-text">toNetwork StartUps</span>
          </div>
        </div>
        <div className="orkut-topbar-right">
          <input
            type="text"
            placeholder="buscar startups"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="orkut-search"
          />
          <button
            className="orkut-search-btn"
            onClick={() => setShowCreateForm(true)}
            style={{
              background: "linear-gradient(135deg, #1B4398 0%, #2563B8 100%)",
              color: "#fff",
              border: "none",
              padding: "6.3px 24px",
              fontSize: "0.95em",
              fontWeight: "700",
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all 0.3s ease",
              boxShadow: "0 4px 12px rgba(27, 67, 152, 0.3)",
              textTransform: "lowercase",
              display: "flex",
              alignItems: "center",
              gap: "6px"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 16px rgba(27, 67, 152, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(27, 67, 152, 0.3)";
            }}
          >
            <svg viewBox="0 0 24 24" fill="white" width="18" height="18">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
            </svg>
            criar startup
          </button>
        </div>
      </div>

      {/* Header Mobile - EXATAMENTE igual ao Home */}
      <div className="orkut-topbar startup-topbar-mobile">
        <div className="orkut-topbar-left" style={{ display: 'none' }}>
          <a href="#" onClick={(e) => { e.preventDefault(); onBack(); }}>Home</a>
        </div>
        <div className="orkut-topbar-network">
          <div className="orkut-topbar-left-content">
            <span className="orkut-network-text">toNetwork StartUps</span>
          </div>
          <div className="orkut-topbar-mobile-buttons">
            <button className="orkut-mobile-search-btn" onClick={() => setShowMobileSearch(!showMobileSearch)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
            </button>
            <button className="orkut-mobile-add-btn" onClick={() => setShowCreateForm(true)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Barra de Pesquisa Mobile - EXATAMENTE igual ao Home */}
        {showMobileSearch && (
          <div className="orkut-mobile-search-bar">
            <div className="orkut-mobile-search-bar-content">
              <button 
                className="orkut-mobile-search-close" 
                onClick={() => {
                  setShowMobileSearch(false);
                  setSearchTerm('');
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
              <input
                type="text"
                placeholder="Buscar startups..."
                className="orkut-mobile-search-bar-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>
          </div>
        )}
      </div>

      <div className="orkut-content">
        
        {/* Sidebar Esquerda - Desktop */}
        <aside className="orkut-sidebar-left">
          <div 
            className="orkut-avatar"
            style={{
              backgroundImage: `url(${profileData?.avatar || "https://i.pinimg.com/736x/d4/8f/8f/d48f8f59f2de25750b13084b59301201.jpg"})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
          <div className="orkut-user-name">{profileData?.nome || user?.email}</div>
          <div className="orkut-invisible">● online</div>
          
          {/* Sistema de Elogios */}
          <div className="orkut-elogios-box">
            <div className="orkut-elogio-titulo">Elogios</div>
            <div className="orkut-elogio-item">
              <span className="orkut-elogio-label">⭐ Legal</span>
              <span className="orkut-elogio-nota">{profileData?.elogios?.legal || 0}</span>
            </div>
            <div className="orkut-elogio-item">
              <span className="orkut-elogio-label">💙 Confiável</span>
              <span className="orkut-elogio-nota">{profileData?.elogios?.confiavel || 0}</span>
            </div>
            <div className="orkut-elogio-item">
              <span className="orkut-elogio-label">🎨 Criativo</span>
              <span className="orkut-elogio-nota">{profileData?.elogios?.criativo || 0}</span>
            </div>
            <div className="orkut-elogio-item">
              <span className="orkut-elogio-label">🧠 Inteligente</span>
              <span className="orkut-elogio-nota">{profileData?.elogios?.inteligente || 0}</span>
            </div>
            <div className="orkut-elogio-item">
              <span className="orkut-elogio-label">😊 Simpático</span>
              <span className="orkut-elogio-nota">{profileData?.elogios?.simpatico || 0}</span>
            </div>
          </div>
          
          {/* Filtros de StartUps - Desktop */}
          <div className="orkut-profile-links" style={{ display: window.innerWidth <= 768 ? 'none' : 'flex' }}>
            <a href="#" onClick={(e) => { e.preventDefault(); onNav("profile"); }}>
              editar perfil
            </a>
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); setActiveTab("explore"); }}
              className={activeTab === "explore" ? "active" : ""}
            >
              explorar
            </a>
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); setActiveTab("updates"); }}
              className={activeTab === "updates" ? "active" : ""}
            >
              atualizações
            </a>
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); setActiveTab("jobs"); }}
              className={activeTab === "jobs" ? "active" : ""}
            >
              vagas
            </a>
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); setActiveTab("following"); }}
              className={activeTab === "following" ? "active" : ""}
            >
              seguindo ({followedStartups.length})
            </a>
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); setActiveTab("my-startups"); }}
              className={activeTab === "my-startups" ? "active" : ""}
            >
              minhas ({myStartups.length})
            </a>
            <a 
              href="#" 
              onClick={async (e) => { 
                e.preventDefault(); 
                await supabase.auth.signOut();
                window.location.reload();
              }} 
              style={{color: '#d73527'}}
            >
              sair
            </a>
          </div>

          {/* Informações do Perfil */}
          <div className="orkut-profile-info" style={{ display: window.innerWidth <= 768 ? 'none' : 'block' }}>
            <p><strong>Relacionamento:</strong> {profileData?.status || "não informado"}</p>
            <p><strong>Aniversário:</strong> {profileData?.aniversario || "não informado"}</p>
            <p><strong>Cidade:</strong> {profileData?.cidade || "não informado"}</p>
          </div>
        </aside>

        {/* Conteúdo Principal */}
        <main className="orkut-center">

        {/* Abas de filtro - APENAS MOBILE */}
        <div className="mobile-filter-bar" style={{ 
          background: "#fff", 
          padding: "10px", 
          borderRadius: "5px", 
          marginBottom: "20px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          display: window.innerWidth <= 768 ? 'flex' : 'none'
        }}>
          <button
            className={`mobile-filter-btn ${activeTab === "explore" ? "active" : ""}`}
            onClick={() => setActiveTab("explore")}
          >
            explorar
          </button>
          <button
            className={`mobile-filter-btn ${activeTab === "updates" ? "active" : ""}`}
            onClick={() => setActiveTab("updates")}
          >
            atualizações
          </button>
          <button
            className={`mobile-filter-btn ${activeTab === "jobs" ? "active" : ""}`}
            onClick={() => setActiveTab("jobs")}
          >
            vagas
          </button>
          <button
            className={`mobile-filter-btn ${activeTab === "following" ? "active" : ""}`}
            onClick={() => setActiveTab("following")}
          >
            seguindo ({followedStartups.length})
          </button>
          <button
            className={`mobile-filter-btn ${activeTab === "my-startups" ? "active" : ""}`}
            onClick={() => setActiveTab("my-startups")}
          >
            minhas ({myStartups.length})
          </button>
        </div>

        {/* Conteúdo principal */}
        <div>
          {activeTab === "explore" && (
            <div>
              <div style={{ 
                background: "#1B4398",
                padding: "15px 20px",
                borderRadius: "5px",
                marginBottom: "20px",
                color: "#fff",
                border: "1px solid #164080"
              }}>
                <h2 style={{ margin: "0 0 3px 0", fontSize: "1.3em", fontWeight: "600" }}>
                  explorar startups
                </h2>
                <p style={{ margin: 0, opacity: 0.9, fontSize: "0.85em" }}>
                  Descubra startups inovadoras criadas pela comunidade
                </p>
              </div>
              {startups.filter(s => 
                s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                s.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (s.category && s.category.toLowerCase().includes(searchTerm.toLowerCase()))
              ).length > 0 ? (
                startups.filter(s => 
                  s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  s.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  (s.category && s.category.toLowerCase().includes(searchTerm.toLowerCase()))
                ).map(renderStartupCard)
              ) : (
                <div className="orkut-box" style={{ 
                  textAlign: "center", 
                  padding: "40px 20px",
                  background: "#F9FAFB",
                  borderRadius: "5px"
                }}>
                  <p style={{ color: "#999", margin: 0, fontSize: "1.1em" }}>
                    {searchTerm ? "Nenhuma startup encontrada com essa busca" : "Nenhuma startup cadastrada ainda. Seja o primeiro!"}
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === "updates" && (
            <div>
              <div style={{ 
                background: "#1B4398",
                padding: "15px 20px",
                borderRadius: "5px",
                marginBottom: "20px",
                color: "#fff",
                border: "1px solid #164080"
              }}>
                <h2 style={{ margin: "0 0 3px 0", fontSize: "1.3em", fontWeight: "600" }}>
                  atualizações
                </h2>
                <p style={{ margin: 0, opacity: 0.9, fontSize: "0.85em" }}>
                  Últimas novidades das startups que você segue
                </p>
              </div>
              {followedStartups.length > 0 ? (
                followedStartups.flatMap(startup => 
                  (startup.updates || []).map(update => ({
                    ...update,
                    startup_info: { id: startup.id, name: startup.name, logo_url: startup.logo_url }
                  }))
                ).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).length > 0 ? (
                  followedStartups.flatMap(startup => 
                    (startup.updates || []).map(update => ({
                      ...update,
                      startup_info: { id: startup.id, name: startup.name, logo_url: startup.logo_url }
                    }))
                  ).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map(update => (
                    <div key={update.id} className="orkut-box" style={{ 
                      marginBottom: "15px",
                      background: "#fff",
                      border: "1px solid #D9E1EE",
                      borderRadius: "12px",
                      padding: "20px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      cursor: "pointer",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease"
                    }}
                    onClick={() => loadStartupDetails(update.startup_info.id)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.05)";
                    }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                        {update.startup_info.logo_url ? (
                          <img src={update.startup_info.logo_url} alt={update.startup_info.name} style={{ width: "40px", height: "40px", borderRadius: "6px", objectFit: "contain", background: "white", padding: "3px", border: "2px solid #E8F0FE" }} />
                        ) : (
                          <div style={{ width: "40px", height: "40px", borderRadius: "6px", background: "#1B4398", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", fontSize: "1.2em" }}>
                            {update.startup_info.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: "0 0 4px 0", color: "#1B4398", fontSize: "0.95em", fontWeight: "600" }}>
                            {update.startup_info.name}
                          </h4>
                          <span style={{ fontSize: "0.75em", color: "#999" }}>
                            {new Date(update.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                      </div>
                      <h3 style={{ margin: "0 0 8px 0", color: "#333", fontSize: "1.1em", fontWeight: "600" }}>
                        {update.title}
                      </h3>
                      <p style={{ margin: "0 0 15px 0", color: "#666", lineHeight: "1.5", whiteSpace: "pre-wrap" }}>
                        {update.content}
                      </p>

                      {/* Barra de Interações */}
                      <div 
                        style={{ 
                          borderTop: "1px solid #E8F0FE",
                          paddingTop: "12px",
                          display: "flex",
                          gap: "12px",
                          alignItems: "center"
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLikeUpdate(update.id);
                          }}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "6px 12px",
                            borderRadius: "20px",
                            fontSize: "0.95em",
                            fontWeight: "500",
                            color: updateLikes[update.id]?.userLiked ? "#E74C3C" : "#666",
                            transition: "all 0.2s ease",
                            background: updateLikes[update.id]?.userLiked ? "#FFE8E6" : "#F8F9FA"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = updateLikes[update.id]?.userLiked ? "#FFD6D3" : "#E8F0FE";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = updateLikes[update.id]?.userLiked ? "#FFE8E6" : "#F8F9FA";
                          }}
                        >
                          {updateLikes[update.id]?.userLiked ? (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="#E74C3C">
                              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                            </svg>
                          ) : (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2">
                              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                            </svg>
                          )}
                          <span>{updateLikes[update.id]?.count || 0}</span>
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowComments({...showComments, [update.id]: !showComments[update.id]});
                          }}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "6px 12px",
                            borderRadius: "20px",
                            fontSize: "0.95em",
                            fontWeight: "500",
                            color: "#666",
                            transition: "all 0.2s ease",
                            background: showComments[update.id] ? "#E8F0FE" : "#F8F9FA"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#E8F0FE";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = showComments[update.id] ? "#E8F0FE" : "#F8F9FA";
                          }}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                          </svg>
                          <span>{updateComments[update.id]?.length || 0}</span>
                        </button>
                      </div>

                      {/* Seção de Comentários */}
                      {showComments[update.id] && (
                        <div 
                          style={{
                            borderTop: "1px solid #E8F0FE",
                            marginTop: "12px",
                            paddingTop: "15px"
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Lista de Comentários */}
                          {updateComments[update.id] && updateComments[update.id].length > 0 && (
                            <div style={{ marginBottom: "15px" }}>
                              {updateComments[update.id].map(comment => (
                                <div key={comment.id} style={{
                                  display: "flex",
                                  gap: "10px",
                                  marginBottom: "12px",
                                  padding: "10px",
                                  background: "#F8F9FA",
                                  borderRadius: "8px"
                                }}>
                                  <img 
                                    src={comment.user?.avatar || comment.user?.foto || "https://ui-avatars.com/api/?name=" + (comment.user?.nome || "User")}
                                    alt={comment.user?.nome || "Usuário"}
                                    style={{
                                      width: "32px",
                                      height: "32px",
                                      borderRadius: "50%",
                                      objectFit: "cover",
                                      flexShrink: 0
                                    }}
                                  />
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: "4px", flexWrap: "wrap" }}>
                                        <strong style={{ color: "#1B4398", fontSize: "0.9em" }}>
                                          {comment.user?.nome || "Usuário"}
                                        </strong>
                                        {comment.user?.verified && (
                                          <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="#1DA1F2"
                                            title="Perfil Verificado"
                                          >
                                            <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91s-2.52-1.27-3.91-.81c-.66-1.31-1.91-2.19-3.34-2.19s-2.67.88-3.34 2.19c-1.39-.46-2.9-.2-3.91.81s-1.27 2.52-.81 3.91c-1.31.66-2.19 1.91-2.19 3.34s.88 2.67 2.19 3.34c-.46 1.39-.2 2.9.81 3.91s2.52 1.27 3.91.81c.66 1.31 1.91 2.19 3.34 2.19s2.67-.88 3.34-2.19c1.39.46 2.9.2 3.91-.81s1.27-2.52.81-3.91c1.31-.66 2.19-1.91 2.19-3.34zm-11.71 4.2L6.8 12.46l1.41-1.42L10.54 13.38l5.65-5.65 1.42 1.42-7.12 7.05z"/>
                                          </svg>
                                        )}
                                        <span style={{ 
                                          fontSize: "0.8em", 
                                          color: "#999",
                                          marginLeft: "4px"
                                        }}>
                                          {new Date(comment.created_at).toLocaleDateString("pt-BR", {
                                            day: "2-digit",
                                            month: "short"
                                          })}
                                        </span>
                                      </div>
                                      {comment.user_id === user?.id && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteComment(update.id, comment.id);
                                          }}
                                          style={{
                                            background: "none",
                                            border: "none",
                                            cursor: "pointer",
                                            color: "#999",
                                            padding: "2px 6px",
                                            fontSize: "0.85em",
                                            transition: "color 0.2s ease",
                                            display: "flex",
                                            alignItems: "center"
                                          }}
                                          onMouseEnter={(e) => e.currentTarget.style.color = "#E74C3C"}
                                          onMouseLeave={(e) => e.currentTarget.style.color = "#999"}
                                          title="Deletar comentário"
                                        >
                                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="3,6 5,6 21,6"/>
                                            <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6M8,6V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"/>
                                            <line x1="10" y1="11" x2="10" y2="17"/>
                                            <line x1="14" y1="11" x2="14" y2="17"/>
                                          </svg>
                                        </button>
                                      )}
                                    </div>
                                    <p style={{
                                      margin: "6px 0 0 0",
                                      color: "#333",
                                      fontSize: "0.9em",
                                      lineHeight: "1.4",
                                      wordWrap: "break-word"
                                    }}>
                                      {comment.content}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Input de Novo Comentário */}
                          <div style={{ display: "flex", gap: "10px", alignItems: "start" }}>
                            <img 
                              src={user?.foto || "/default-avatar.png"}
                              alt="Você"
                              style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "50%",
                                objectFit: "cover",
                                flexShrink: 0,
                                marginTop: "4px"
                              }}
                            />
                            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
                              <textarea
                                value={commentText[update.id] || ""}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setCommentText({...commentText, [update.id]: e.target.value});
                                }}
                                onClick={(e) => e.stopPropagation()}
                                placeholder="Escreva um comentário..."
                                style={{
                                  width: "100%",
                                  padding: "10px",
                                  border: "1px solid #D9E1EE",
                                  borderRadius: "8px",
                                  fontSize: "0.9em",
                                  resize: "vertical",
                                  minHeight: "60px",
                                  fontFamily: "inherit"
                                }}
                              />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddComment(update.id);
                                }}
                                disabled={!commentText[update.id]?.trim()}
                                style={{
                                  alignSelf: "flex-end",
                                  padding: "8px 20px",
                                  background: commentText[update.id]?.trim() ? "#1B4398" : "#ccc",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: "20px",
                                  cursor: commentText[update.id]?.trim() ? "pointer" : "not-allowed",
                                  fontSize: "0.9em",
                                  fontWeight: "500",
                                  transition: "all 0.2s ease"
                                }}
                                onMouseEnter={(e) => {
                                  if (commentText[update.id]?.trim()) {
                                    e.currentTarget.style.background = "#153470";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (commentText[update.id]?.trim()) {
                                    e.currentTarget.style.background = "#1B4398";
                                  }
                                }}
                              >
                                Comentar
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="orkut-box" style={{ 
                    textAlign: "center", 
                    padding: "40px 20px",
                    background: "#F9FAFB",
                    borderRadius: "5px"
                  }}>
                    <p style={{ color: "#999", margin: 0, fontSize: "1.1em" }}>
                      Nenhuma atualização ainda. Comece a seguir startups para ver as novidades aqui!
                    </p>
                  </div>
                )
              ) : (
                <div className="orkut-box" style={{ 
                  textAlign: "center", 
                  padding: "40px 20px",
                  background: "#F9FAFB",
                  borderRadius: "5px"
                }}>
                  <p style={{ color: "#999", margin: 0, fontSize: "1.1em" }}>
                    Você ainda não segue nenhuma startup. Explore e comece a seguir!
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === "jobs" && (
            <div>
              <div style={{ 
                background: "#28A745",
                padding: "15px 20px",
                borderRadius: "5px",
                marginBottom: "20px",
                color: "#fff",
                border: "1px solid #218838"
              }}>
                <h2 style={{ margin: "0 0 3px 0", fontSize: "1.3em", fontWeight: "600" }}>
                  vagas
                </h2>
                <p style={{ margin: 0, opacity: 0.9, fontSize: "0.85em" }}>
                  Oportunidades das startups que você segue
                </p>
              </div>
              {followedStartups.length > 0 ? (
                followedStartups.flatMap(startup => 
                  (startup.jobs || []).map(job => ({
                    ...job,
                    startup_info: { id: startup.id, name: startup.name, logo_url: startup.logo_url }
                  }))
                ).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).length > 0 ? (
                  followedStartups.flatMap(startup => 
                    (startup.jobs || []).map(job => ({
                      ...job,
                      startup_info: { id: startup.id, name: startup.name, logo_url: startup.logo_url }
                    }))
                  ).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map(job => (
                    <div key={job.id} className="orkut-box" style={{ 
                      marginBottom: "15px",
                      background: "#fff",
                      border: "1px solid #D9E1EE",
                      borderRadius: "12px",
                      padding: "20px",
                      borderLeft: "4px solid #28A745",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      cursor: "pointer",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease"
                    }}
                    onClick={() => loadStartupDetails(job.startup_info.id)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.05)";
                    }}
                    >
                      {/* Header: Logo da startup + Nome */}
                      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "15px" }}>
                        {job.startup_info.logo_url ? (
                          <img src={job.startup_info.logo_url} alt={job.startup_info.name} style={{ width: "50px", height: "50px", borderRadius: "8px", objectFit: "contain", background: "white", padding: "4px", border: "2px solid #E8F0FE" }} />
                        ) : (
                          <div style={{ width: "50px", height: "50px", borderRadius: "8px", background: "#28A745", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", fontSize: "1.2em" }}>
                            {job.startup_info.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: "0 0 4px 0", color: "#28A745", fontSize: "0.95em", fontWeight: "600" }}>
                            {job.startup_info.name}
                          </h4>
                          <span style={{ fontSize: "0.75em", color: "#999" }}>
                            {new Date(job.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                      </div>

                      {/* Título da vaga */}
                      <h3 style={{ margin: "0 0 10px 0", color: "#1B4398", fontSize: "1.2em", fontWeight: "600" }}>
                        {job.title}
                      </h3>

                      {/* Descrição */}
                      <p style={{ margin: "0 0 12px 0", color: "#666", lineHeight: "1.6", whiteSpace: "pre-wrap" }}>
                        {job.description}
                      </p>

                      {/* Detalhes da vaga */}
                      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "12px" }}>
                        {job.type && (
                          <span style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                            padding: "6px 12px",
                            background: "#E8F5E9",
                            borderRadius: "20px",
                            fontSize: "0.8em",
                            fontWeight: "500",
                            color: "#28A745"
                          }}>
                            <svg viewBox="0 0 24 24" fill="#28A745" width="14" height="14">
                              <path d="M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z"/>
                            </svg>
                            {job.type}
                          </span>
                        )}
                        {job.salary_range && (
                          <span style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                            padding: "6px 12px",
                            background: "#E3F2FD",
                            borderRadius: "20px",
                            fontSize: "0.8em",
                            fontWeight: "500",
                            color: "#1B4398"
                          }}>
                            <svg viewBox="0 0 24 24" fill="#1B4398" width="14" height="14">
                              <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
                            </svg>
                            {job.salary_range}
                          </span>
                        )}
                        {job.location && (
                          <span style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                            padding: "6px 12px",
                            background: "#FFF3E0",
                            borderRadius: "20px",
                            fontSize: "0.8em",
                            fontWeight: "500",
                            color: "#F57C00"
                          }}>
                            <svg viewBox="0 0 24 24" fill="#F57C00" width="14" height="14">
                              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                            </svg>
                            {job.location}
                          </span>
                        )}
                      </div>

                      {/* Requisitos */}
                      {job.requirements && (
                        <div style={{
                          background: "#F5F9FF",
                          padding: "12px",
                          borderRadius: "8px",
                          marginTop: "10px"
                        }}>
                          <strong style={{ 
                            fontSize: "0.9em", 
                            color: "#1B4398",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px"
                          }}>
                            <svg viewBox="0 0 24 24" fill="#1B4398" width="16" height="16">
                              <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
                            </svg>
                            Requisitos:
                          </strong>
                          <p style={{ 
                            margin: "8px 0 0 0", 
                            fontSize: "0.85em", 
                            whiteSpace: "pre-wrap",
                            color: "#666",
                            lineHeight: "1.5"
                          }}>
                            {job.requirements}
                          </p>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="orkut-box" style={{ 
                    textAlign: "center", 
                    padding: "40px 20px",
                    background: "#F9FAFB",
                    borderRadius: "5px"
                  }}>
                    <p style={{ color: "#999", margin: 0, fontSize: "1.1em" }}>
                      Nenhuma vaga disponível no momento. As startups que você segue ainda não publicaram vagas!
                    </p>
                  </div>
                )
              ) : (
                <div className="orkut-box" style={{ 
                  textAlign: "center", 
                  padding: "40px 20px",
                  background: "#F9FAFB",
                  borderRadius: "5px"
                }}>
                  <p style={{ color: "#999", margin: 0, fontSize: "1.1em" }}>
                    Você ainda não segue nenhuma startup. Explore e comece a seguir para ver as vagas!
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === "following" && (
            <div>
              <div style={{ 
                background: "#D4418E",
                padding: "15px 20px",
                borderRadius: "5px",
                marginBottom: "20px",
                color: "#fff",
                border: "1px solid #B53578"
              }}>
                <h2 style={{ margin: "0 0 3px 0", fontSize: "1.3em", fontWeight: "600" }}>
                  seguindo
                </h2>
                <p style={{ margin: 0, opacity: 0.9, fontSize: "0.85em" }}>
                  StartUps que você acompanha ({followedStartups.length})
                </p>
              </div>
              {followedStartups.filter(s => 
                s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                s.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (s.category && s.category.toLowerCase().includes(searchTerm.toLowerCase()))
              ).length > 0 ? (
                followedStartups.filter(s => 
                  s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  s.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  (s.category && s.category.toLowerCase().includes(searchTerm.toLowerCase()))
                ).map(renderStartupCard)
              ) : (
                <div className="orkut-box" style={{ 
                  textAlign: "center", 
                  padding: "40px 20px",
                  background: "#F9FAFB",
                  borderRadius: "5px"
                }}>
                  <p style={{ color: "#999", margin: 0, fontSize: "1.1em" }}>
                    {searchTerm ? "Nenhuma startup encontrada com essa busca" : "Você ainda não segue nenhuma startup. Explore e comece a seguir!"}
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === "my-startups" && (
            <div>
              <div style={{ 
                background: "#28A745",
                padding: "15px 20px",
                borderRadius: "5px",
                marginBottom: "20px",
                color: "#fff",
                border: "1px solid #218838"
              }}>
                <h2 style={{ margin: "0 0 3px 0", fontSize: "1.3em", fontWeight: "600" }}>
                  suas startups
                </h2>
                <p style={{ margin: 0, opacity: 0.9, fontSize: "0.85em" }}>
                  Gerencie suas startups ({myStartups.length})
                </p>
              </div>
              {myStartups.filter(s => 
                s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                s.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (s.category && s.category.toLowerCase().includes(searchTerm.toLowerCase()))
              ).length > 0 ? (
                myStartups.filter(s => 
                  s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  s.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  (s.category && s.category.toLowerCase().includes(searchTerm.toLowerCase()))
                ).map(renderStartupCard)
              ) : (
                <div className="orkut-box" style={{ 
                  textAlign: "center", 
                  padding: "40px 20px",
                  background: "#F9FAFB",
                  borderRadius: "5px"
                }}>
                  {searchTerm ? (
                    <p style={{ color: "#999", margin: 0, fontSize: "1.1em" }}>
                      Nenhuma startup encontrada com essa busca
                    </p>
                  ) : (
                    <>
                      <p style={{ color: "#999", margin: 0, fontSize: "1.1em"}}>
                        Você ainda não criou nenhuma startup
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Aba de Comunidade Detalhada */}
          {activeTab === "community-detail" && communityStartup && (
            <div>
              {/* Cabeçalho da comunidade */}
              <div style={{
                background: "#fff",
                borderRadius: "12px",
                padding: "20px",
                marginBottom: "20px",
                border: "1px solid #D9E1EE",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                  {communityStartup.logo_url && (
                    <img 
                      src={communityStartup.logo_url} 
                      alt={communityStartup.name}
                      style={{
                        width: "60px",
                        height: "60px",
                        borderRadius: "12px",
                        objectFit: "cover"
                      }}
                    />
                  )}
                  <div>
                    <h2 style={{ margin: "0 0 5px 0", color: "#333" }}>{communityStartup.name}</h2>
                    <p style={{ margin: 0, color: "#666", fontSize: "0.9em" }}>Comunidade</p>
                  </div>
                </div>
              </div>
              
              {/* Conteúdo da comunidade (copiar do modal) */}
              {/* Campo para postar */}
              <div style={{
                background: "#fff",
                borderRadius: "12px",
                padding: isMobile ? "15px" : "15px",
                marginBottom: "15px",
                border: "1px solid #D9E1EE",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
              }}>
                <textarea
                  value={communityPostText}
                  onChange={(e) => setCommunityPostText(e.target.value)}
                  placeholder={isMobile ? "Compartilhe com a comunidade..." : "Compartilhe novidades, dúvidas ou discussões com a comunidade da startup..."}
                  style={{
                    width: "100%",
                    maxWidth: "100%",
                    minHeight: isMobile ? "80px" : "80px",
                    padding: isMobile ? "12px" : "12px",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    fontSize: isMobile ? "0.9em" : "1em",
                    resize: "vertical",
                    fontFamily: "inherit",
                    marginBottom: "10px",
                    boxSizing: "border-box"
                  }}
                />
                <button
                  onClick={() => handlePostToCommunity()}
                  disabled={!communityPostText.trim()}
                  style={{
                    background: communityPostText.trim() ? "#667eea" : "#ccc",
                    color: "#fff",
                    border: "none",
                    padding: isMobile ? "12px 16px" : "10px 20px",
                    borderRadius: "8px",
                    fontSize: isMobile ? "0.9em" : "0.95em",
                    fontWeight: "600",
                    cursor: communityPostText.trim() ? "pointer" : "not-allowed",
                    transition: "all 0.2s",
                    width: isMobile ? "100%" : "auto",
                    minHeight: isMobile ? "44px" : "auto"
                  }}
                >
                  Publicar
                </button>
              </div>

              {/* Posts da comunidade */}
              {loadingCommunity ? (
                <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                  Carregando comunidade...
                </div>
              ) : communityPosts.length === 0 ? (
                <div style={{
                  background: "#fff",
                  borderRadius: "12px",
                  padding: "40px",
                  textAlign: "center",
                  border: "1px solid #D9E1EE"
                }}>
                  <p style={{ color: "#999", margin: 0 }}>Nenhuma publicação ainda. Seja o primeiro a compartilhar!</p>
                </div>
              ) : (
                communityPosts.map(post => {
                  // Processar reações do post
                  const postReactions = communityReactions[post.id] || [];
                  const reactionCounts = {};
                  postReactions.forEach(reaction => {
                    reactionCounts[reaction.reaction_type] = (reactionCounts[reaction.reaction_type] || 0) + 1;
                  });

                  return (
                  <div
                    key={post.id}
                    onTouchStart={() => {
                      if (isMobile && post.author_id === user?.id) {
                        const timer = setTimeout(() => {
                          setConfirmModal({
                            message: "Deseja excluir este post?",
                            onConfirm: () => handleDeleteCommunityPost(post.id)
                          });
                        }, 500);
                        setLongPressTimer(timer);
                      }
                    }}
                    onTouchEnd={() => {
                      if (longPressTimer) {
                        clearTimeout(longPressTimer);
                        setLongPressTimer(null);
                      }
                    }}
                    onTouchMove={() => {
                      if (longPressTimer) {
                        clearTimeout(longPressTimer);
                        setLongPressTimer(null);
                      }
                    }}
                    style={{
                      background: "#fff",
                      borderRadius: "12px",
                      padding: isMobile ? "12px" : "15px",
                      marginBottom: "15px",
                      border: "1px solid #D9E1EE",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      position: "relative"
                    }}
                  >
                    {/* Cabeçalho do post */}
                    <div style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      marginBottom: "10px"
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}>
                        <img
                          src={post.author?.avatar || "https://via.placeholder.com/40"}
                          alt={post.author?.nome}
                          style={{
                            width: isMobile ? "36px" : "40px",
                            height: isMobile ? "36px" : "40px",
                            minWidth: isMobile ? "36px" : "40px",
                            minHeight: isMobile ? "36px" : "40px",
                            maxWidth: isMobile ? "36px" : "40px",
                            maxHeight: isMobile ? "36px" : "40px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            flexShrink: 0,
                            display: "block"
                          }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            flexWrap: "wrap"
                          }}>
                            <strong style={{
                              fontSize: isMobile ? "0.9em" : "1em",
                              color: "#333",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap"
                            }}>
                              {post.author?.nome || "Usuário"}
                            </strong>
                            {post.author?.verified && (
                              <svg 
                                viewBox="0 0 24 24" 
                                fill="#1DA1F2" 
                                width={isMobile ? "14" : "16"}
                                height={isMobile ? "14" : "16"}
                                style={{ 
                                  minWidth: isMobile ? "14px" : "16px",
                                  minHeight: isMobile ? "14px" : "16px",
                                  maxWidth: isMobile ? "14px" : "16px",
                                  maxHeight: isMobile ? "14px" : "16px",
                                  flexShrink: 0,
                                  display: "inline-block",
                                  verticalAlign: "middle"
                                }}
                              >
                                <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z"/>
                              </svg>
                            )}
                          </div>
                          <div style={{
                            fontSize: isMobile ? "0.75em" : "0.8em",
                            color: "#999"
                          }}>
                            {getTimeAgo(post.created_at)}
                          </div>
                        </div>
                      </div>

                      {/* Menu de 3 pontos - apenas desktop */}
                      {!isMobile && post.author_id === user?.id && (
                        <div style={{ position: "relative" }}>
                          <button
                            onClick={() => togglePostMenu(post.id)}
                            style={{
                              background: "transparent",
                              border: "none",
                              fontSize: "1.2em",
                              cursor: "pointer",
                              color: "#999",
                              padding: "5px"
                            }}
                          >
                            ⋮
                          </button>
                          {openMenus[post.id] && (
                            <div style={{
                              position: "absolute",
                              right: 0,
                              top: "30px",
                              background: "#fff",
                              border: "1px solid #ddd",
                              borderRadius: "8px",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                              zIndex: 100,
                              minWidth: "150px"
                            }}>
                              <button
                                onClick={() => {
                                  setConfirmModal({
                                    message: "Tem certeza que deseja excluir este post?",
                                    onConfirm: () => handleDeleteCommunityPost(post.id)
                                  });
                                  togglePostMenu(post.id);
                                }}
                                style={{
                                  width: "100%",
                                  padding: "10px 15px",
                                  border: "none",
                                  background: "transparent",
                                  textAlign: "left",
                                  cursor: "pointer",
                                  color: "#e53e3e",
                                  fontSize: "0.9em"
                                }}
                              >
                                🗑️ Excluir
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Conteúdo do post */}
                    <p style={{
                      color: "#555",
                      lineHeight: "1.6",
                      marginBottom: "15px",
                      fontSize: isMobile ? "0.9em" : "1em",
                      wordBreak: "break-word"
                    }}>
                      {post.content}
                    </p>

                    {/* Reações */}
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: isMobile ? "8px" : "10px",
                      marginBottom: "12px",
                      flexWrap: "wrap",
                      position: "relative"
                    }}>
                      <button
                        onClick={() => toggleReactionPicker(post.id)}
                        style={{
                          background: "#f0f4ff",
                          border: "1px solid #d0d9f0",
                          borderRadius: "20px",
                          padding: isMobile ? "6px 12px" : "5px 12px",
                          fontSize: isMobile ? "0.8em" : "0.85em",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          color: "#667eea",
                          fontWeight: "500",
                          transition: "all 0.2s",
                          minHeight: isMobile ? "36px" : "auto",
                          whiteSpace: "nowrap"
                        }}
                      >
                        😊 Reagir
                      </button>

                      {Object.keys(reactionCounts).length > 0 && (
                        <div style={{
                          display: "flex",
                          gap: isMobile ? "6px" : "8px",
                          flexWrap: "wrap"
                        }}>
                          {Object.entries(reactionCounts).map(([emoji, count]) => (
                            <span
                              key={emoji}
                              style={{
                                background: "#f8f9fa",
                                padding: isMobile ? "4px 10px" : "4px 10px",
                                borderRadius: "15px",
                                fontSize: isMobile ? "0.75em" : "0.8em",
                                color: "#666",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                minHeight: isMobile ? "28px" : "auto"
                              }}
                            >
                              {emoji} {count}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Picker de reações */}
                      {showReactionPicker === post.id && (
                        <div style={{
                          position: "absolute",
                          background: "#fff",
                          border: "1px solid #ddd",
                          borderRadius: "25px",
                          padding: isMobile ? "8px" : "8px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          display: "flex",
                          gap: isMobile ? "8px" : "8px",
                          zIndex: 1000,
                          top: isMobile ? "40px" : "40px",
                          left: 0
                        }}>
                          {["👍", "❤️", "😄", "🎉", "🚀"].map(emoji => (
                            <button
                              key={emoji}
                              onClick={() => handleCommunityReaction(post.id, emoji)}
                              style={{
                                background: "transparent",
                                border: "none",
                                fontSize: isMobile ? "1.5em" : "1.5em",
                                cursor: "pointer",
                                padding: isMobile ? "4px" : "5px",
                                transition: "transform 0.2s",
                                minWidth: isMobile ? "40px" : "auto",
                                minHeight: isMobile ? "40px" : "auto",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                              }}
                              onMouseEnter={(e) => e.target.style.transform = "scale(1.3)"}
                              onMouseLeave={(e) => e.target.style.transform = "scale(1)"}
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Botão de comentários */}
                    <button
                      onClick={() => {
                        if (!communityComments[post.id]) {
                          loadCommunityComments(post.id);
                        }
                        setCollapsedComments(prev => ({
                          ...prev,
                          [post.id]: prev[post.id] === undefined ? false : !prev[post.id]
                        }));
                      }}
                      style={{
                        background: "transparent",
                        border: "1px solid #ddd",
                        borderRadius: "20px",
                        padding: isMobile ? "8px 14px" : "6px 12px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        fontSize: isMobile ? "0.85em" : "0.9em",
                        transition: "all 0.2s",
                        minWidth: isMobile ? "44px" : "auto",
                        minHeight: isMobile ? "44px" : "auto",
                        justifyContent: "center",
                        marginBottom: "10px"
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#f5f5f5"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                    >
                      <svg 
                        viewBox="0 0 24 24" 
                        fill="currentColor" 
                        width="16" 
                        height="16"
                        style={{
                          minWidth: "16px",
                          minHeight: "16px",
                          maxWidth: "16px",
                          maxHeight: "16px",
                          flexShrink: 0,
                          display: "block"
                        }}
                      >
                        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
                      </svg>
                      <span>{(communityComments[post.id] || []).length || 0}</span>
                    </button>

                    {/* Seção de comentários */}
                    {communityComments[post.id] && collapsedComments[post.id] === false && (
                      <div style={{
                        marginTop: "12px",
                        paddingTop: "12px",
                        borderTop: "1px solid #f0f0f0"
                      }}>
                        {/* Comentários existentes */}
                        {communityComments[post.id].filter(c => !c.parent_comment_id).map(comment => {
                          const replies = communityComments[post.id].filter(r => r.parent_comment_id === comment.id);
                          const replyKey = `${post.id}_${comment.id}`;
                          
                          return (
                          <div key={comment.id}>
                            {/* Comentário principal */}
                            <div
                              onTouchStart={() => {
                                if (isMobile && comment.author_id === user?.id) {
                                  const timer = setTimeout(() => {
                                    setConfirmModal({
                                      message: "Deseja excluir este comentário?",
                                      onConfirm: () => handleDeleteComment(comment.id)
                                    });
                                  }, 500);
                                  setLongPressTimer(timer);
                                }
                              }}
                              onTouchEnd={() => {
                                if (longPressTimer) {
                                  clearTimeout(longPressTimer);
                                  setLongPressTimer(null);
                                }
                              }}
                              onTouchMove={() => {
                                if (longPressTimer) {
                                  clearTimeout(longPressTimer);
                                  setLongPressTimer(null);
                                }
                              }}
                              style={{
                                marginBottom: "12px",
                                padding: isMobile ? "8px" : "10px",
                                background: "#f8f9fa",
                                borderRadius: "8px",
                                position: "relative"
                              }}
                            >
                              <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                marginBottom: "6px"
                              }}>
                                <div style={{ display: "flex", gap: isMobile ? "5px" : "6px", flex: 1 }}>
                                  <img
                                    src={comment.author?.avatar || "https://via.placeholder.com/32"}
                                    alt={comment.author?.nome}
                                    style={{
                                      width: isMobile ? "18px" : "22px",
                                      height: isMobile ? "18px" : "22px",
                                      minWidth: isMobile ? "18px" : "22px",
                                      minHeight: isMobile ? "18px" : "22px",
                                      maxWidth: isMobile ? "18px" : "22px",
                                      maxHeight: isMobile ? "18px" : "22px",
                                      borderRadius: "50%",
                                      objectFit: "cover",
                                      flexShrink: 0,
                                      display: "block"
                                    }}
                                  />
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: "inline-flex", alignItems: "center", gap: "3px" }}>
                                      <strong style={{ 
                                        fontSize: isMobile ? "0.8em" : "0.85em", 
                                        color: "#333"
                                      }}>
                                        {comment.author?.nome}
                                      </strong>
                                      {comment.author?.verified && (
                                        <svg 
                                          viewBox="0 0 24 24" 
                                          fill="#1DA1F2" 
                                          width={isMobile ? "12" : "14"}
                                          height={isMobile ? "12" : "14"}
                                          style={{ 
                                            minWidth: isMobile ? "12px" : "14px",
                                            minHeight: isMobile ? "12px" : "14px",
                                            maxWidth: isMobile ? "12px" : "14px",
                                            maxHeight: isMobile ? "12px" : "14px",
                                            flexShrink: 0,
                                            display: "inline-block",
                                            verticalAlign: "middle"
                                          }}
                                        >
                                          <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z"/>
                                        </svg>
                                      )}
                                    </div>
                                    <div style={{ fontSize: isMobile ? "0.65em" : "0.7em", color: "#999", marginTop: "2px" }}>
                                      {getTimeAgo(comment.created_at)}
                                    </div>
                                  </div>
                                </div>

                                {/* Menu de 3 pontos para comentário - apenas desktop */}
                                {!isMobile && comment.author_id === user?.id && (
                                  <div style={{ position: "relative" }}>
                                    <button
                                      onClick={() => togglePostMenu(`comment-${comment.id}`)}
                                      style={{
                                        background: "transparent",
                                        border: "none",
                                        fontSize: "1em",
                                        cursor: "pointer",
                                        color: "#999",
                                        padding: "2px"
                                      }}
                                    >
                                      ⋮
                                    </button>
                                    {openMenus[`comment-${comment.id}`] && (
                                      <div style={{
                                        position: "absolute",
                                        right: 0,
                                        top: "25px",
                                        background: "#fff",
                                        border: "1px solid #ddd",
                                        borderRadius: "8px",
                                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                        zIndex: 100,
                                        minWidth: "130px"
                                      }}>
                                        <button
                                          onClick={() => {
                                            setConfirmModal({
                                              message: "Tem certeza que deseja excluir este comentário?",
                                              onConfirm: () => handleDeleteCommunityComment(comment.id)
                                            });
                                            togglePostMenu(`comment-${comment.id}`);
                                          }}
                                          style={{
                                            width: "100%",
                                            padding: "8px 12px",
                                            border: "none",
                                            background: "transparent",
                                            textAlign: "left",
                                            cursor: "pointer",
                                            color: "#e53e3e",
                                            fontSize: "0.85em"
                                          }}
                                        >
                                          🗑️ Excluir
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>

                              <p style={{
                                color: "#555",
                                fontSize: isMobile ? "0.85em" : "0.9em",
                                lineHeight: "1.5",
                                margin: "8px 0",
                                wordBreak: "break-word"
                              }}>
                                {comment.content}
                              </p>

                              <button
                                onClick={() => setReplyingTo(prev => ({
                                  ...prev,
                                  [`${post.id}_${comment.id}`]: !prev[`${post.id}_${comment.id}`]
                                }))}
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  color: "#667eea",
                                  fontSize: isMobile ? "0.75em" : "0.8em",
                                  cursor: "pointer",
                                  fontWeight: "500",
                                  padding: "4px 0"
                                }}
                              >
                                {replyingTo[`${post.id}_${comment.id}`] ? "Cancelar" : "Responder"}
                              </button>

                              {/* Campo de resposta */}
                              {replyingTo[`${post.id}_${comment.id}`] && (
                                <div style={{ marginTop: "10px" }}>
                                  <textarea
                                    value={communityCommentText[`${post.id}_reply_${comment.id}`] || ""}
                                    onChange={(e) => setCommunityCommentText(prev => ({
                                      ...prev,
                                      [`${post.id}_reply_${comment.id}`]: e.target.value
                                    }))}
                                    placeholder="Escreva sua resposta..."
                                    style={{
                                      width: "100%",
                                      maxWidth: "100%",
                                      minHeight: isMobile ? "60px" : "60px",
                                      padding: isMobile ? "10px" : "8px",
                                      border: "1px solid #ddd",
                                      borderRadius: "8px",
                                      fontSize: isMobile ? "0.85em" : "0.85em",
                                      resize: "vertical",
                                      fontFamily: "inherit",
                                      marginBottom: "8px",
                                      boxSizing: "border-box"
                                    }}
                                  />
                                  <button
                                    onClick={() => handleReply(post.id, comment.id)}
                                    disabled={!communityCommentText[`${post.id}_reply_${comment.id}`]?.trim()}
                                    style={{
                                      background: communityCommentText[`${post.id}_reply_${comment.id}`]?.trim() ? "#667eea" : "#ccc",
                                      color: "#fff",
                                      border: "none",
                                      padding: isMobile ? "10px 16px" : "7px 14px",
                                      borderRadius: "8px",
                                      fontSize: isMobile ? "0.85em" : "0.8em",
                                      fontWeight: "600",
                                      cursor: communityCommentText[`${post.id}_reply_${comment.id}`]?.trim() ? "pointer" : "not-allowed",
                                      transition: "all 0.2s",
                                      minHeight: isMobile ? "40px" : "auto"
                                    }}
                                  >
                                    Responder
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Respostas (replies) */}
                            {replies.length > 0 && (
                              <div style={{
                                marginLeft: isMobile ? "20px" : "30px",
                                marginTop: "8px"
                              }}>
                                {replies.map(reply => (
                              <div
                                key={reply.id}
                                onTouchStart={() => {
                                  if (isMobile && reply.author_id === user?.id) {
                                    const timer = setTimeout(() => {
                                      setConfirmModal({
                                        message: "Deseja excluir esta resposta?",
                                        onConfirm: () => handleDeleteCommunityComment(reply.id)
                                      });
                                    }, 500);
                                    setLongPressTimer(timer);
                                  }
                                }}
                                onTouchEnd={() => {
                                  if (longPressTimer) {
                                    clearTimeout(longPressTimer);
                                    setLongPressTimer(null);
                                  }
                                }}
                                onTouchMove={() => {
                                  if (longPressTimer) {
                                    clearTimeout(longPressTimer);
                                    setLongPressTimer(null);
                                  }
                                }}
                                style={{
                                  marginLeft: isMobile ? "20px" : "30px",
                                  marginBottom: "10px",
                                  padding: isMobile ? "8px" : "10px",
                                  background: "#fff",
                                  border: "1px solid #e8e8e8",
                                  borderRadius: "8px",
                                  position: "relative"
                                }}
                              >
                                <div style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  marginBottom: "6px"
                                }}>
                                  <div style={{ display: "flex", gap: isMobile ? "4px" : "5px", flex: 1 }}>
                                    <img
                                      src={reply.author?.avatar || "https://via.placeholder.com/32"}
                                      alt={reply.author?.nome}
                                      style={{
                                        width: isMobile ? "14px" : "16px",
                                        height: isMobile ? "14px" : "16px",
                                        minWidth: isMobile ? "14px" : "16px",
                                        minHeight: isMobile ? "14px" : "16px",
                                        maxWidth: isMobile ? "14px" : "16px",
                                        maxHeight: isMobile ? "14px" : "16px",
                                        borderRadius: "50%",
                                        objectFit: "cover",
                                        flexShrink: 0,
                                        display: "block"
                                      }}
                                    />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ display: "inline-flex", alignItems: "center", gap: "2px" }}>
                                        <strong style={{ 
                                          fontSize: isMobile ? "0.68em" : "0.75em", 
                                          color: "#333"
                                        }}>
                                          {reply.author?.nome}
                                        </strong>
                                        {reply.author?.verified && (
                                          <svg 
                                            viewBox="0 0 24 24" 
                                            fill="#1DA1F2" 
                                            width="12" 
                                            height="12" 
                                            style={{ 
                                              minWidth: "12px",
                                              minHeight: "12px",
                                              maxWidth: "12px",
                                              maxHeight: "12px",
                                              flexShrink: 0,
                                              display: "inline-block",
                                              verticalAlign: "middle"
                                            }}
                                          >
                                            <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z"/>
                                          </svg>
                                        )}
                                      </div>
                                      <div style={{ fontSize: isMobile ? "0.58em" : "0.62em", color: "#999", marginTop: "1px" }}>
                                        {getTimeAgo(reply.created_at)}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Menu de 3 pontos para resposta - apenas desktop */}
                                  {!isMobile && reply.author_id === user?.id && (
                                    <div style={{ position: "relative" }}>
                                      <button
                                        onClick={() => togglePostMenu(`reply-${reply.id}`)}
                                        style={{
                                          background: "transparent",
                                          border: "none",
                                          fontSize: "0.9em",
                                          cursor: "pointer",
                                          color: "#999",
                                          padding: "2px"
                                        }}
                                      >
                                        ⋮
                                      </button>
                                      {openMenus[`reply-${reply.id}`] && (
                                        <div style={{
                                          position: "absolute",
                                          right: 0,
                                          top: "20px",
                                          background: "#fff",
                                          border: "1px solid #ddd",
                                          borderRadius: "8px",
                                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                          zIndex: 100,
                                          minWidth: "130px"
                                        }}>
                                          <button
                                            onClick={() => {
                                              setConfirmModal({
                                                message: "Tem certeza que deseja excluir esta resposta?",
                                                onConfirm: () => handleDeleteCommunityComment(reply.id)
                                              });
                                              togglePostMenu(`reply-${reply.id}`);
                                            }}
                                            style={{
                                              width: "100%",
                                              padding: "8px 12px",
                                              border: "none",
                                              background: "transparent",
                                              textAlign: "left",
                                              cursor: "pointer",
                                              color: "#e53e3e",
                                              fontSize: "0.85em"
                                            }}
                                          >
                                            🗑️ Excluir
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>

                                <p style={{
                                  color: "#555",
                                  fontSize: isMobile ? "0.8em" : "0.85em",
                                  lineHeight: "1.5",
                                  margin: 0,
                                  wordBreak: "break-word"
                                }}>
                                  {reply.content}
                                </p>
                              </div>
                                ))}
                              </div>
                            )}
                          </div>
                          );
                        })}

                        {/* Campo para novo comentário */}
                        <div style={{ marginTop: "12px" }}>
                          <textarea
                            value={communityCommentText[post.id] || ""}
                            onChange={(e) => setCommunityCommentText(prev => ({
                              ...prev,
                              [post.id]: e.target.value
                            }))}
                            placeholder="Escreva um comentário..."
                            style={{
                              width: "100%",
                              maxWidth: "100%",
                              minHeight: isMobile ? "60px" : "60px",
                              padding: isMobile ? "10px" : "8px",
                              border: "1px solid #ddd",
                              borderRadius: "8px",
                              fontSize: isMobile ? "0.85em" : "0.85em",
                              resize: "vertical",
                              fontFamily: "inherit",
                              marginBottom: "8px",
                              boxSizing: "border-box"
                            }}
                          />
                          <button
                            onClick={() => handleComment(post.id)}
                            disabled={!communityCommentText[post.id]?.trim()}
                            style={{
                              background: communityCommentText[post.id]?.trim() ? "#667eea" : "#ccc",
                              color: "#fff",
                              border: "none",
                              padding: isMobile ? "10px 16px" : "7px 14px",
                              borderRadius: "8px",
                              fontSize: isMobile ? "0.85em" : "0.8em",
                              fontWeight: "600",
                              cursor: communityCommentText[post.id]?.trim() ? "pointer" : "not-allowed",
                              transition: "all 0.2s",
                              minHeight: isMobile ? "40px" : "auto"
                            }}
                          >
                            Comentar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })
              )}
            </div>
          )}
        </div>
        </main>

        {/* Sidebar Direita - Desktop */}
        <aside className="orkut-sidebar-right">
          <div className="orkut-amigos">
            <strong style={{ fontSize: '1em', color: '#003366', marginBottom: '12px', display: 'block' }}>
              Minhas StartUps ({myStartups.length})
            </strong>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '10px' }}>
              {myStartups.slice(0, 5).map(startup => (
                <div 
                  key={startup.id} 
                  style={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    cursor: 'pointer',
                    padding: '12px',
                    borderRadius: '8px',
                    background: '#f9fafb',
                    border: '1px solid #E8F0FE',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                  }}
                  onClick={() => loadStartupDetails(startup.id)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#fff';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f9fafb';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Header com logo e nome */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {startup.logo_url ? (
                      <img 
                        src={startup.logo_url} 
                        alt={startup.name}
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '8px',
                          objectFit: 'cover',
                          border: '2px solid #E8F0FE',
                          flexShrink: 0
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #1B4398 0%, #2557B5 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.1em',
                        fontWeight: 'bold',
                        color: '#fff',
                        flexShrink: 0,
                        border: '2px solid #E8F0FE'
                      }}>
                        {startup.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        fontSize: '0.9em', 
                        fontWeight: '600',
                        color: '#1B4398',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        marginBottom: '2px'
                      }}>
                        {startup.name}
                      </div>
                      {startup.category && (
                        <div style={{
                          fontSize: '0.7em',
                          color: '#666',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {startup.category}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div style={{ 
                    display: 'flex', 
                    gap: '8px',
                    paddingTop: '8px',
                    borderTop: '1px solid #E8F0FE',
                    fontSize: '0.75em',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="#1B4398">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                      </svg>
                      <span style={{ fontWeight: '600', color: '#1B4398' }}>
                        {startup.followers?.[0]?.count || 0}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="#1B4398">
                        <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
                      </svg>
                      <span style={{ fontWeight: '600', color: '#1B4398' }}>
                        {startup.updates?.[0]?.count || 0}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="#1B4398">
                        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 9h-2V5h2v6zm0 4h-2v-2h2v2z"/>
                      </svg>
                      <span style={{ fontWeight: '600', color: '#1B4398' }}>
                        {startup.community_posts?.[0]?.count || 0}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
              {myStartups.length === 0 && (
                <div style={{ 
                  fontSize: '0.85em', 
                  color: '#666', 
                  padding: '20px 10px', 
                  textAlign: 'center',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px dashed #ddd'
                }}>
                  Você ainda não criou nenhuma startup
                </div>
              )}
            </div>
          </div>

          <div className="orkut-amigos" style={{ marginTop: '20px' }}>
            <strong style={{ fontSize: '1em', color: '#003366', marginBottom: '12px', display: 'block' }}>
              Seguindo ({followedStartups.length})
            </strong>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '10px' }}>
              {followedStartups.slice(0, 5).map(startup => (
                <div 
                  key={startup.id} 
                  style={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    cursor: 'pointer',
                    padding: '12px',
                    borderRadius: '8px',
                    background: '#f9fafb',
                    border: '1px solid #E8F0FE',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                  }}
                  onClick={() => loadStartupDetails(startup.id)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#fff';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f9fafb';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Header com logo e nome */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {startup.logo_url ? (
                      <img 
                        src={startup.logo_url} 
                        alt={startup.name}
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '8px',
                          objectFit: 'cover',
                          border: '2px solid #E8F0FE',
                          flexShrink: 0
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #D4418E 0%, #E91E63 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.1em',
                        fontWeight: 'bold',
                        color: '#fff',
                        flexShrink: 0,
                        border: '2px solid #E8F0FE'
                      }}>
                        {startup.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        fontSize: '0.9em', 
                        fontWeight: '600',
                        color: '#D4418E',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        marginBottom: '2px'
                      }}>
                        {startup.name}
                      </div>
                      {startup.category && (
                        <div style={{
                          fontSize: '0.7em',
                          color: '#666',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {startup.category}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div style={{ 
                    display: 'flex', 
                    gap: '8px',
                    paddingTop: '8px',
                    borderTop: '1px solid #E8F0FE',
                    fontSize: '0.75em',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="#D4418E">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                      </svg>
                      <span style={{ fontWeight: '600', color: '#D4418E' }}>
                        {startup.followers?.[0]?.count || 0}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="#D4418E">
                        <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
                      </svg>
                      <span style={{ fontWeight: '600', color: '#D4418E' }}>
                        {startup.updates?.[0]?.count || 0}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="#D4418E">
                        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 9h-2V5h2v6zm0 4h-2v-2h2v2z"/>
                      </svg>
                      <span style={{ fontWeight: '600', color: '#D4418E' }}>
                        {startup.community_posts?.[0]?.count || 0}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
              {followedStartups.length === 0 && (
                <div style={{ 
                  fontSize: '0.85em', 
                  color: '#666', 
                  padding: '20px 10px', 
                  textAlign: 'center',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px dashed #ddd'
                }}>
                  Você não está seguindo nenhuma startup ainda
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>

      {/* Modal de criar startup */}
      {showCreateForm && (
        <div className="orkut-modal-bg" onClick={() => setShowCreateForm(false)}>
          <div 
            className={window.innerWidth <= 768 ? "orkut-modal-mobile-fullscreen" : "orkut-modal"} 
            onClick={(e) => e.stopPropagation()} 
            style={window.innerWidth > 768 ? { maxWidth: "600px", maxHeight: "90vh", overflowY: "auto" } : {}}
          >
            <div className="orkut-modal-header">
              <h2>Criar Nova StartUp</h2>
              <button className="orkut-modal-close" onClick={() => setShowCreateForm(false)}>
                ×
              </button>
            </div>
            <div className="orkut-modal-content">
            <form onSubmit={handleCreateStartup}>
              <div className="orkut-form-group">
                <label>Nome da StartUp *</label>
                <input
                  type="text"
                  className="orkut-input"
                  placeholder="Digite o nome da startup"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="orkut-form-group">
                <label>Descrição *</label>
                <textarea
                  className="orkut-textarea"
                  placeholder="Descreva sua startup de forma clara e objetiva"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="4"
                  required
                />
              </div>

              <div style={{ 
                display: "grid", 
                gridTemplateColumns: window.innerWidth > 768 ? "1fr 1fr" : "1fr", 
                gap: "15px" 
              }}>
                <div className="orkut-form-group">
                  <label>Categoria</label>
                  <input
                    type="text"
                    className="orkut-input"
                    placeholder="Ex: Tecnologia"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  />
                </div>

                <div className="orkut-form-group">
                  <label>Localização</label>
                  <input
                    type="text"
                    className="orkut-input"
                    placeholder="Cidade, Estado"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  />
                </div>
              </div>

              <div className="orkut-form-group">
                <label>Website</label>
                <input
                  type="url"
                  className="orkut-input"
                  placeholder="https://suastartup.com"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                />
              </div>

              <div className="orkut-form-group">
                <label>Logo da Startup</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  style={{ display: "none" }}
                  id="logo-upload"
                />
                <label 
                  htmlFor="logo-upload" 
                  className="orkut-btn-secondary"
                  style={{ 
                    display: "inline-block",
                    textAlign: "center",
                    cursor: "pointer",
                    marginBottom: logoPreview ? "10px" : "0"
                  }}
                >
                  {logoPreview ? "Trocar Logo" : "Escolher Logo"}
                </label>
                {logoPreview && (
                  <div style={{ marginTop: "10px", textAlign: "center" }}>
                    <img 
                      src={logoPreview} 
                      alt="Preview" 
                      style={{ 
                        maxWidth: "150px",
                        maxHeight: "150px",
                        borderRadius: "8px",
                        border: "2px solid #ddd"
                      }} 
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setLogoFile(null);
                        setLogoPreview(null);
                      }}
                      style={{
                        display: "block",
                        margin: "10px auto 0",
                        background: "#dc3545",
                        color: "#fff",
                        border: "none",
                        padding: "6px 12px",
                        borderRadius: "6px",
                        cursor: "pointer"
                      }}
                    >
                      Remover
                    </button>
                  </div>
                )}
                <p style={{ fontSize: "0.85em", color: "#666", marginTop: "8px" }}>
                  Ou cole uma URL abaixo:
                </p>
                <input
                  type="url"
                  className="orkut-input"
                  placeholder="https://exemplo.com/logo.png"
                  value={formData.logo_url}
                  onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                />
              </div>

              <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                <button 
                  type="submit" 
                  className="orkut-btn"
                  style={{ flex: 1 }}
                >
                  Criar StartUp
                </button>
                <button
                  type="button"
                  className="orkut-btn-secondary"
                  onClick={() => setShowCreateForm(false)}
                  style={{ flex: 1 }}
                >
                  Cancelar
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de postar atualização */}
      {showUpdateForm && selectedStartup && (
        <div className="orkut-modal-bg" onClick={() => setShowUpdateForm(false)} style={{ zIndex: 1100 }}>
          <div 
            className={window.innerWidth <= 768 ? "orkut-modal-mobile-fullscreen" : "orkut-modal"} 
            onClick={(e) => e.stopPropagation()} 
            style={window.innerWidth > 768 ? { maxWidth: "600px", maxHeight: "90vh", overflowY: "auto" } : {}}
          >
            <div className="orkut-modal-header">
              <h2>
                Postar Atualização{window.innerWidth > 768 ? ` - ${selectedStartup.name}` : ''}
              </h2>
              <button className="orkut-modal-close" onClick={() => setShowUpdateForm(false)}>
                ×
              </button>
            </div>
            <div className="orkut-modal-content">
            <form onSubmit={handlePostUpdate}>
              <div className="orkut-form-group">
                <label>Título *</label>
                <input
                  type="text"
                  className="orkut-input"
                  placeholder="Título da atualização"
                  value={updateData.title}
                  onChange={(e) => setUpdateData({ ...updateData, title: e.target.value })}
                  required
                />
              </div>

              <div className="orkut-form-group">
                <label>Conteúdo *</label>
                <textarea
                  className="orkut-textarea"
                  placeholder="Escreva sobre as novidades da sua startup"
                  value={updateData.content}
                  onChange={(e) => setUpdateData({ ...updateData, content: e.target.value })}
                  rows="6"
                  required
                />
              </div>

              <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                <button type="submit" className="orkut-btn" style={{ flex: 1 }}>
                  Publicar
                </button>
                <button
                  type="button"
                  className="orkut-btn-secondary"
                  onClick={() => setShowUpdateForm(false)}
                  style={{ flex: 1 }}
                >
                  Cancelar
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de divulgar vaga */}
      {showJobForm && selectedStartup && (
        <div className="orkut-modal-bg" onClick={() => setShowJobForm(false)} style={{ zIndex: 1100 }}>
          <div 
            className={window.innerWidth <= 768 ? "orkut-modal-mobile-fullscreen" : "orkut-modal"} 
            onClick={(e) => e.stopPropagation()} 
            style={window.innerWidth > 768 ? { maxWidth: "650px", maxHeight: "90vh", overflowY: "auto" } : {}}
          >
            <div className="orkut-modal-header">
              <h2>
                Divulgar Vaga{window.innerWidth > 768 ? ` - ${selectedStartup.name}` : ''}
              </h2>
              <button className="orkut-modal-close" onClick={() => setShowJobForm(false)}>
                ×
              </button>
            </div>
            <div className="orkut-modal-content">
            <form onSubmit={handlePostJob}>
              <div className="orkut-form-group">
                <label>Título da Vaga *</label>
                <input
                  type="text"
                  className="orkut-input"
                  placeholder="Ex: Desenvolvedor Full Stack"
                  value={jobData.title}
                  onChange={(e) => setJobData({ ...jobData, title: e.target.value })}
                  required
                />
              </div>

              <div className="orkut-form-group">
                <label>Descrição *</label>
                <textarea
                  className="orkut-textarea"
                  placeholder="Descreva as responsabilidades e atividades da vaga"
                  value={jobData.description}
                  onChange={(e) => setJobData({ ...jobData, description: e.target.value })}
                  rows="4"
                  required
                />
              </div>

              <div className="orkut-form-group">
                <label>Requisitos</label>
                <textarea
                  className="orkut-textarea"
                  placeholder="Liste os requisitos necessários..."
                  value={jobData.requirements}
                  onChange={(e) => setJobData({ ...jobData, requirements: e.target.value })}
                  rows="3"
                />
              </div>

              <div className="orkut-form-group">
                <label>Tipo de Contrato</label>
                <select
                  className="orkut-input"
                  value={jobData.type}
                  onChange={(e) => setJobData({ ...jobData, type: e.target.value })}
                >
                  <option value="full-time">Tempo Integral</option>
                  <option value="part-time">Meio Período</option>
                  <option value="contract">Contrato</option>
                  <option value="internship">Estágio</option>
                  <option value="volunteer">Voluntário</option>
                </select>
              </div>

              <div style={{ 
                display: "grid", 
                gridTemplateColumns: window.innerWidth > 768 ? "1fr 1fr" : "1fr", 
                gap: "15px" 
              }}>
                <div className="orkut-form-group">
                  <label>Localização</label>
                  <input
                    type="text"
                    className="orkut-input"
                    placeholder="Cidade ou Remoto"
                    value={jobData.location}
                    onChange={(e) => setJobData({ ...jobData, location: e.target.value })}
                  />
                </div>

                <div className="orkut-form-group">
                  <label>Faixa Salarial</label>
                  <input
                    type="text"
                    className="orkut-input"
                    placeholder="Ex: R$ 3.000 - R$ 5.000"
                    value={jobData.salary_range}
                    onChange={(e) => setJobData({ ...jobData, salary_range: e.target.value })}
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                <button type="submit" className="orkut-btn" style={{ flex: 1 }}>
                  Publicar Vaga
                </button>
                <button
                  type="button"
                  className="orkut-btn-secondary"
                  onClick={() => setShowJobForm(false)}
                  style={{ flex: 1 }}
                >
                  Cancelar
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de detalhes da startup */}
      {renderStartupDetails()}

      {/* Notificação de Mensagem */}
      {messageNotification && (
        <div 
          onClick={() => {
            onNav("home");
            setMessageNotification(null);
          }}
          className="message-notification-desktop"
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            width: '90%',
            maxWidth: '380px',
            zIndex: 9999,
            cursor: 'pointer',
            animation: 'slideUpFromBottom 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
            border: '1px solid rgba(0,0,0,0.06)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            backdropFilter: 'blur(10px)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
            e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.15), 0 4px 12px rgba(0,0,0,0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0) scale(1)';
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)';
          }}
        >
          {/* Avatar com Badge Online */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <img 
              src={messageNotification.senderPhoto}
              alt={messageNotification.senderName}
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid #f0f0f0'
              }}
            />
            <div style={{
              position: 'absolute',
              bottom: '0',
              right: '0',
              width: '14px',
              height: '14px',
              backgroundColor: '#00b894',
              borderRadius: '50%',
              border: '2px solid #ffffff',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}/>
          </div>

          {/* Conteúdo */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ 
              fontWeight: '600', 
              fontSize: '14px',
              color: '#2d3436',
              marginBottom: '3px',
              letterSpacing: '-0.01em'
            }}>
              {messageNotification.senderName}
            </div>
            <div style={{ 
              fontSize: '13px',
              color: '#636e72',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: '1.4'
            }}>
              {messageNotification.message}
            </div>
          </div>

          {/* Botão Fechar */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMessageNotification(null);
            }}
            style={{
              background: '#f1f3f5',
              border: 'none',
              fontSize: '14px',
              cursor: 'pointer',
              borderRadius: '50%',
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#636e72',
              transition: 'all 0.2s ease',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#e9ecef';
              e.currentTarget.style.color = '#2d3436';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#f1f3f5';
              e.currentTarget.style.color = '#636e72';
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} />}

      {/* Modal de confirmação */}
      {confirmModal && (
        <ConfirmModal
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}

      {/* Barra de Navegação Mobile - igual ao Home */}
      <div className="orkut-mobile-nav">
        <button className="orkut-mobile-nav-btn" onClick={onBack}>
          <svg viewBox="0 0 24 24" fill="white" width="24" height="24">
            <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
          </svg>
          <span>Home</span>
        </button>
      </div>
    </div>
  );
}

export default StartUps;


